
import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { BrowserRouter as Router, Route, Routes, Link, useLocation, Navigate } from 'react-router-dom';
import { MessageSquarePlus, Settings, Ticket, Moon, Sun, Loader2, X, Maximize2, Minimize2, ChevronDown, ChevronUp } from 'lucide-react';
import { AccountCircle } from '@mui/icons-material';
import { auth, db } from './firebaseConfig';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { UserProvider, useUserHits } from './contexts/UserContext';
import { analytics, trackEvent, setUserProperties } from './services/analytics';
import { useAnalytics } from './hooks/useAnalytics';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet-async';
import './i18n';
import LanguageSwitcher from './components/LanguageSwitcher';
import LanguageWrapper from './components/LanguageWrapper';
import LanguageRedirect from './components/LanguageRedirect';
import { useLocalizedPath } from './utils/navigation';
import LocalizedLink from './components/LocalizedLink';
import ShareWithFriends from './components/ShareWithFriends';

// Import all components
import MainPage from './components/MainPage';
import ChatPage from './components/ChatPage';
import GlobalRiskPage from './components/GlobalRiskPage';
import AuthPage from './components/AuthPage';
import SignUpPage from './components/SignPage';
import PricingSection from './components/PricingSection';
import Footer from './components/Footer';
import PlanDetailsPage from './components/PlansDetailPage';
import ContactPage from './components/ContactPage';
import PrivacyPage from './components/PrivacyPolicy';
import TermsPage from './components/TermsCondition';
import PricingPage from './components/PricingPolicy';
import SettingsPage from './components/SettingsPage';
import SettingsMemoryPage from './components/SettingsMemoryPage';
import UnifiedPrivacyConsent from './components/UnifiedPrivacyConsent';
import DocumentationPage from './components/Documentation';
import UseCasesComponent from './components/UseCases';
import SarvalokGovernmentBenefits from './components/GovtPage';
import SarvalokForEducation from './components/EduPage';
import SarvalokForInvestors from './components/InvestPage';
import SarvalokForCommunities from './components/OrgPage';
import SarvalokFeatures from './components/FeaturePage';
import SarvalokSecurity from './components/Security';
import SarvalokEnterprise from './components/Enterprise';
import AboutSarvalok from './components/Aboutus';
import HallucinationLandingPage from './components/HallucinationLandingPage';
import ResetPswd from './components/ResetPaswd';
import Ticker from './components/Ticker';
import BlogPage from './components/BlogPage';
import OCRPage from './components/OCRPage';
import { Layout as AISuiteLayout } from './components/ai-suite/Layout';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/toaster';
import FocusLock from 'react-focus-lock';
import FeedbackModal, { FeedbackType } from './components/feedback/FeedbackModal';
import { submitFeedback, getSecureHeaders } from './services';
import { useTheme } from './components/ThemeProvider';
import { isValidAdMode } from './utils/adLandingIntent';
import { useClarityTags } from './hooks/useClarityTags';
import SplashGate from './components/SplashGate';
import {
  cacheAuthUser,
  clearCachedAuthUser,
  ensureAuthUserDoc,
  ensureUserErrorMessage,
  isEnsureUserBlockedError,
  needsUserDocRepair,
  providerIdForUser,
} from './utils/authUserDoc';
import './components/componentDarkSurfaceFixes.css';

const WhySarvalokPage = React.lazy(() => import('./components/WhySarvalokPage'));
const AISuiteDashboard = React.lazy(() => import('./components/ai-suite/pages/Dashboard'));
const AISuiteOCRPage = React.lazy(() => import('./components/ai-suite/pages/OCRPage'));
const AISuiteChatPage = React.lazy(() => import('./components/ai-suite/pages/ChatPage'));
const AISuiteWriterPage = React.lazy(() => import('./components/ai-suite/pages/WriterPage'));
const AISuiteCodePage = React.lazy(() => import('./components/ai-suite/pages/CodePage'));
const AISuiteSummarizerPage = React.lazy(() => import('./components/ai-suite/pages/SummarizerPage'));
const AISuiteEmailPage = React.lazy(() => import('./components/ai-suite/pages/EmailPage'));
const AISuiteSQLPage = React.lazy(() => import('./components/ai-suite/pages/SQLPage'));
const AISuiteGrammarPage = React.lazy(() => import('./components/ai-suite/pages/GrammarPage'));
const AISuiteTranslatorPage = React.lazy(() => import('./components/ai-suite/pages/TranslatorPage'));
const AISuiteStoryPage = React.lazy(() => import('./components/ai-suite/pages/StoryPage'));
const AISuiteQuizPage = React.lazy(() => import('./components/ai-suite/pages/QuizPage'));
const AISuiteResumePage = React.lazy(() => import('./components/ai-suite/pages/ResumePage'));
const AISuiteSocialMediaPage = React.lazy(() => import('./components/ai-suite/pages/SocialMediaPage'));
const AISuiteRecipePage = React.lazy(() => import('./components/ai-suite/pages/RecipePage'));
const AISuiteFinancePage = React.lazy(() => import('./components/ai-suite/pages/FinancePage'));
const AISuiteMeetingNotesPage = React.lazy(() => import('./components/ai-suite/pages/MeetingNotesPage'));
const AISuiteSentimentPage = React.lazy(() => import('./components/ai-suite/pages/SentimentPage'));
const AISuiteInterviewPage = React.lazy(() => import('./components/ai-suite/pages/InterviewPage'));
const AISuiteImageGeneratorPage = React.lazy(() => import('./components/ai-suite/pages/ImageGeneratorPage'));
const SuperAdminDashboard = React.lazy(() => import('./components/enterprise/SuperAdminDashboard'));
const OrgAdminDashboard = React.lazy(() => import('./components/enterprise/OrgAdminDashboard'));
const InviteLandingPage = React.lazy(() => import('./components/enterprise/InviteLandingPage'));
const PaymentSuccessPage = React.lazy(() => import('./components/PaymentSuccessPage'));

type ViewTransitionHandle = {
  ready: Promise<void>;
  finished: Promise<void>;
};

type DocumentWithViewTransition = Document & {
  startViewTransition?: (callback: () => void) => ViewTransitionHandle;
};


// NotFound Component
const NotFound = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8">
        <h1 className="text-6xl font-bold text-gray-800">404</h1>
        <h2 className="text-2xl font-semibold text-gray-600 mt-4">{t('error:page_not_found')}</h2>
        <p className="text-gray-500 mt-2">{t('error:page_not_found_description')}</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/" 
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {t('error:back_to_home')}
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// ServiceUnavailable Component
const ServiceUnavailable = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8">
        <h1 className="text-6xl font-bold text-gray-800">503</h1>
        <h2 className="text-2xl font-semibold text-gray-600 mt-4">{t('error:service_unavailable')}</h2>
        <p className="text-gray-500 mt-2">{t('error:service_unavailable_description')}</p>
        <p className="text-gray-500">{t('error:try_again_later')}</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/" 
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {t('error:back_to_home')}
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// LegacyRedirect Component for backward compatibility (preserve query/hash)
const LegacyRedirect = ({ to }: { to: string }) => {
  const { search, hash } = window.location;
  return <Navigate to={`${to}${search}${hash}`} replace />;
};

// LegacyRedirectSlug preserves slug and query/hash when redirecting to English namespace
const LegacyRedirectSlug = () => {
  const url = new URL(window.location.href);
  const parts = url.pathname.split('/');
  const slug = parts[parts.length - 1] || '';
  return <Navigate to={`/en/blog/${slug}${url.search}${url.hash}`} replace />;
};

// Redirect bare /invite/:id to /en/invite/:id
const LegacyRedirectInvite = () => {
  const url = new URL(window.location.href);
  const parts = url.pathname.split('/');
  const inviteId = parts[parts.length - 1] || '';
  return <Navigate to={`/en/invite/${inviteId}${url.search}${url.hash}`} replace />;
};

type ExploreFeatureKey = 'rigveda' | 'chant';

const EXPLORE_FEATURE_STORAGE_KEYS: Record<ExploreFeatureKey, string> = {
  rigveda: 'sarvalok_explore_seen_rigveda_v1',
  chant: 'sarvalok_explore_seen_chant_v1',
};

const getExploreFeatureSeenState = () => {
  if (typeof window === 'undefined') {
    return { rigveda: false, chant: false };
  }

  try {
    return {
      rigveda: window.localStorage.getItem(EXPLORE_FEATURE_STORAGE_KEYS.rigveda) === '1',
      chant: window.localStorage.getItem(EXPLORE_FEATURE_STORAGE_KEYS.chant) === '1',
    };
  } catch {
    return { rigveda: false, chant: false };
  }
};

const ExploreFeatureDot = ({ className = '' }: { className?: string }) => (
  <span
    aria-hidden="true"
    className={`inline-block h-2 w-2 shrink-0 rounded-full bg-orange-500 shadow-[0_0_0_3px_rgba(249,115,22,0.16)] dark:shadow-[0_0_0_3px_rgba(251,146,60,0.16)] ${className}`}
  />
);

const PaymentPendingDot = ({ className = '', label }: { className?: string; label: string }) => (
  <span
    title={label}
    className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full bg-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.18)] ${className}`}
  >
    <span className="sr-only">{label}</span>
  </span>
);

const formatSubscriptionNameForDisplay = (subscriptionName: string | null) => {
  const trimmed = subscriptionName?.trim();
  if (!trimmed) return '';
  const readable = trimmed.replace(/_/g, ' ');
  if (readable === readable.toLowerCase()) {
    return readable.replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return readable;
};

const PremiumAvatarFace = ({
  user,
  isPaidPlan,
  variant = 'desktop',
}: {
  user: any;
  isPaidPlan: boolean;
  variant?: 'desktop' | 'drawer';
}) => {
  const initial = user?.displayName?.trim() ? user.displayName.trim()[0].toUpperCase() : null;
  const wrapperClasses = variant === 'drawer' ? 'h-11 w-11' : 'h-8 w-8';
  const faceClasses =
    variant === 'drawer'
      ? 'h-11 w-11 bg-blue-600 text-white text-base'
      : 'h-8 w-8 bg-gray-300 text-gray-900 text-sm';
  const face = (
    <span className={`flex items-center justify-center overflow-hidden rounded-full font-medium ${faceClasses}`}>
      {initial || <AccountCircle fontSize={variant === 'drawer' ? 'medium' : 'small'} />}
    </span>
  );

  if (!isPaidPlan) return face;

  return (
    <span className={`relative inline-flex items-center justify-center ${wrapperClasses}`}>
      <span className="sv-premium-avatar-ring pointer-events-none absolute -inset-[3px]" aria-hidden="true" />
      <span className="pointer-events-none absolute -inset-[1px] rounded-full bg-white dark:bg-gray-950" aria-hidden="true" />
      <span className="relative inline-flex">
        {face}
      </span>
    </span>
  );
};

const identifyClarityUser = (uid: string) => {
  if (typeof window === 'undefined') return;
  try {
    const clarity = window.clarity;
    if (typeof clarity === 'function') {
      clarity('identify', uid);
    }
  } catch {
    // Analytics should never block auth or navigation.
  }
};

// Strips the merged "Related audio/media" block — the [MEDIA:...] tokens AND the
// "Related audio:" header that introduces them — from an answer. The Experimental
// surface must never surface the media player (parity with the mobile Sanskrit
// Lab). RAG/Fusion output is unchanged; this is a pure render-time filter.
function stripMediaSection(text: string): string {
  if (!text || text.indexOf('[MEDIA:') === -1) return text || '';
  let body = text
    .replace(/\[MEDIA:\d+:(?:audio|video)\][ \t]*[^\n\r]*/g, '')
    .replace(/^(?:\s*\[[A-Za-z][A-Za-z0-9 _-]{1,40}\]\s*)+$/gm, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  body = body.replace(/\n*[ \t>*_#-]*Related\b[^\n:]{0,40}:[ \t*_]*(?=\n|$)/gi, '');
  return body.replace(/\s+$/, '');
}

function ExperimentalOverlay({ onClose }: { onClose: () => void }) {
  const { i18n } = useTranslation();
  const API_BASE_URL = import.meta.env.VITE_APP_SERVER;
  const [termQuery, setTermQuery] = useState('');
  const [termAnswer, setTermAnswer] = useState('');
  const [termThought, setTermThought] = useState('');
  const [termLoading, setTermLoading] = useState(false);
  const [termError, setTermError] = useState<string | null>(null);
  const [termProgressActive, setTermProgressActive] = useState(false);
  const [termProgressStartMs, setTermProgressStartMs] = useState<number | null>(null);
  const [termProgressPct, setTermProgressPct] = useState(0);

  const [deepQuery, setDeepQuery] = useState('');
  const [deepAnswer, setDeepAnswer] = useState('');
  const [deepThought, setDeepThought] = useState('');
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepError, setDeepError] = useState<string | null>(null);
  const [deepProgressActive, setDeepProgressActive] = useState(false);
  const [deepProgressStartMs, setDeepProgressStartMs] = useState<number | null>(null);
  const [deepProgressPct, setDeepProgressPct] = useState(0);
  const [activeTab, setActiveTab] = useState<'term' | 'inquiry'>('term');
  const [maximizedPanel, setMaximizedPanel] = useState<null | 'term' | 'inquiry'>(null);

  useEffect(() => {
    setTermError(null);
    setDeepError(null);
  }, []);

  const markdownComponents = {
    table: ({ ...props }: any) => (
      <div className="w-full overflow-x-auto">
        <table {...props} className={`w-full table-fixed ${props.className || ''}`.trim()} />
      </div>
    ),
    th: ({ ...props }: any) => (
      <th {...props} className={`break-words ${props.className || ''}`.trim()} />
    ),
    td: ({ ...props }: any) => (
      <td {...props} className={`break-words ${props.className || ''}`.trim()} />
    ),
  };

  useEffect(() => {
    if (!termLoading || !termProgressActive || termProgressStartMs == null) return;
    let raf = 0;
    const tick = () => {
      const elapsedMs = Date.now() - termProgressStartMs;
      const pct = Math.max(0, Math.min(100, (elapsedMs / 60000) * 100));
      setTermProgressPct(pct);
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [termLoading, termProgressActive, termProgressStartMs]);

  useEffect(() => {
    if (!deepLoading || !deepProgressActive || deepProgressStartMs == null) return;
    let raf = 0;
    const tick = () => {
      const elapsedMs = Date.now() - deepProgressStartMs;
      const pct = Math.max(0, Math.min(100, (elapsedMs / 60000) * 100));
      setDeepProgressPct(pct);
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [deepLoading, deepProgressActive, deepProgressStartMs]);

  useEffect(() => {
    if (termThought.trim().length > 0) setTermProgressActive(false);
  }, [termThought]);

  useEffect(() => {
    if (deepThought.trim().length > 0) setDeepProgressActive(false);
  }, [deepThought]);

  const ProgressBar = ({ pct }: { pct: number }) => (
    <div className="h-2 w-32 sm:w-44 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
      <div
        className="h-full bg-gray-900 dark:bg-white transition-[width] duration-150 ease-linear"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );

  const streamRagAnswer = async (opts: {
    endpoint: string;
    prompt: string;
    setAnswer: (value: string) => void;
    setThought?: (value: string) => void;
    setLoading: (value: boolean) => void;
    setError: (value: string | null) => void;
    onThoughtStarted?: () => void;
  }) => {
    if (!opts.prompt.trim()) return;
    opts.setLoading(true);
    opts.setError(null);
    opts.setAnswer('');
    opts.setThought?.('');

    try {
      const headers = await getSecureHeaders();
      const res = await fetch(`${API_BASE_URL}${opts.endpoint}`, {
        method: 'POST',
        headers,
        cache: 'no-store',
        body: JSON.stringify({ user_prompt: opts.prompt.trim(), locale: i18n.language }),
      });
      if (!res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await res.json();
          throw new Error(String(data?.detail || 'Request failed'));
        }
        throw new Error(`Request failed (HTTP ${res.status})`);
      }
      if (!res.body) {
        throw new Error('No response body');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let rawStream = '';
      let sseBuffer = '';
      let treatAsSse = false;
      let answerStarted = false;
      let thoughtStarted = false;
      let finalText = '';
      let thoughtText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        rawStream += chunk;
        if (!treatAsSse && rawStream.trimStart().startsWith('data:')) {
          treatAsSse = true;
        }

        if (!treatAsSse) {
          finalText += chunk;
          if (!answerStarted && finalText.trim().length > 0) {
            answerStarted = true;
            thoughtText = '';
            opts.setThought?.('');
          }
          opts.setAnswer(finalText);
          continue;
        }

        sseBuffer += chunk;
        while (true) {
          const boundaryIndex = sseBuffer.indexOf('\n\n');
          if (boundaryIndex === -1) break;
          const frame = sseBuffer.slice(0, boundaryIndex);
          sseBuffer = sseBuffer.slice(boundaryIndex + 2);

          const dataLines = frame
            .split('\n')
            .map(l => l.trimEnd())
            .filter(l => l.startsWith('data:'));
          if (dataLines.length === 0) continue;
          const dataStr = dataLines.map(l => l.slice(5).trimStart()).join('\n').trim();
          if (!dataStr) continue;

          try {
            const payloadObj = JSON.parse(dataStr);
            if (payloadObj?.type === 'thought_delta' && typeof payloadObj.text === 'string') {
              if (!answerStarted) {
                if (!thoughtStarted && payloadObj.text.trim().length > 0) {
                  thoughtStarted = true;
                  opts.onThoughtStarted?.();
                }
                thoughtText += payloadObj.text;
                opts.setThought?.(thoughtText);
              }
            } else if (payloadObj?.type === 'answer_delta' && typeof payloadObj.text === 'string') {
              finalText += payloadObj.text;
              if (!answerStarted && finalText.trim().length > 0) {
                answerStarted = true;
                thoughtText = '';
                opts.setThought?.('');
              }
              opts.setAnswer(finalText);
            } else if (payloadObj?.type === 'done') {
            } else if (typeof payloadObj?.answer === 'string') {
              const nextAnswer = payloadObj.answer;
              if (nextAnswer.trim().length > 0) {
                finalText = nextAnswer;
                if (!answerStarted) {
                  answerStarted = true;
                  thoughtText = '';
                  opts.setThought?.('');
                }
                opts.setAnswer(finalText);
              }
            }
          } catch {
            finalText += dataStr;
            if (!answerStarted && finalText.trim().length > 0) {
              answerStarted = true;
              thoughtText = '';
              opts.setThought?.('');
            }
            opts.setAnswer(finalText);
          }
        }
      }

      if (!finalText && rawStream && rawStream.trimStart().startsWith('data:')) {
        const lastEvent = rawStream.trim().split('\n\n').filter(Boolean).pop() || '';
        const lastDataLine = lastEvent
          .split('\n')
          .map(l => l.trimEnd())
          .find(l => l.startsWith('data:')) || '';
        const lastDataStr = lastDataLine.replace(/^data:\s*/, '').trim();
        if (lastDataStr) {
          try {
            const parsed = JSON.parse(lastDataStr);
            if (typeof parsed?.answer === 'string') {
              finalText = parsed.answer;
            }
          } catch {}
        }
        if (finalText) opts.setAnswer(finalText);
      }
    } catch (e: any) {
      opts.setError(e?.message ? String(e.message) : 'Request failed');
    } finally {
      opts.setLoading(false);
    }
  };

  const TermPanel = (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Term Deep-Dive
          </h2>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Explore real meaning behind Sanskrit terms
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMaximizedPanel(prev => (prev === 'term' ? null : 'term'))}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
          title={maximizedPanel === 'term' ? 'Restore' : 'Maximize'}
        >
          {maximizedPanel === 'term' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Examples: Dharma, Prāṇa, Ṛta
      </div>

      <input
        value={termQuery}
        onChange={(e) => setTermQuery(e.target.value)}
        placeholder="Enter a term (ex: Dharma, Prāṇa, Ṛta) ..."
        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 px-3 py-2 mb-3"
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setTermProgressPct(0);
            setTermProgressStartMs(Date.now());
            setTermProgressActive(true);
            streamRagAnswer({
              endpoint: '/api/experimental/single_term/stream',
              prompt: termQuery,
              setAnswer: setTermAnswer,
              setThought: setTermThought,
              setLoading: setTermLoading,
              setError: setTermError,
              onThoughtStarted: () => setTermProgressActive(false),
            });
          }}
          disabled={termLoading || !termQuery.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {termLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Research
        </button>
        {termLoading && termProgressActive ? <ProgressBar pct={termProgressPct} /> : null}
      </div>

      {termError ? (
        <div className="mt-4 text-sm text-red-600">
          {termError}
        </div>
      ) : null}

      {termThought ? (
        <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Thinking
          </div>
          <div className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-200">
            {termThought}
          </div>
        </div>
      ) : null}

      <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4 min-h-[260px]">
        <div className="prose dark:prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {stripMediaSection(termAnswer) || (termLoading ? '...' : '')}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );

  const InquiryPanel = (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Vedic Inquiry
          </h2>
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Explore characters, events, symbols
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMaximizedPanel(prev => (prev === 'inquiry' ? null : 'inquiry'))}
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10"
          title={maximizedPanel === 'inquiry' ? 'Restore' : 'Maximize'}
        >
          {maximizedPanel === 'inquiry' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Examples: Who is Hanuman?, Why did Ravana abduct Sita?
      </div>

      <input
        value={deepQuery}
        onChange={(e) => setDeepQuery(e.target.value)}
        placeholder="Enter a question (ex: Who is Hanuman?) ..."
        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 px-3 py-2 mb-3"
      />

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setDeepProgressPct(0);
            setDeepProgressStartMs(Date.now());
            setDeepProgressActive(true);
            streamRagAnswer({
              endpoint: '/api/experimental/deep_research/stream',
              prompt: deepQuery,
              setAnswer: setDeepAnswer,
              setThought: setDeepThought,
              setLoading: setDeepLoading,
              setError: setDeepError,
              onThoughtStarted: () => setDeepProgressActive(false),
            });
          }}
          disabled={deepLoading || !deepQuery.trim()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {deepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Research
        </button>
        {deepLoading && deepProgressActive ? <ProgressBar pct={deepProgressPct} /> : null}
      </div>

      {deepError ? (
        <div className="mt-4 text-sm text-red-600">
          {deepError}
        </div>
      ) : null}

      {deepThought ? (
        <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
            Thinking
          </div>
          <div className="text-sm whitespace-pre-wrap text-gray-700 dark:text-gray-200">
            {deepThought}
          </div>
        </div>
      ) : null}

      <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4 min-h-[260px]">
        <div className="prose dark:prose-invert max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {stripMediaSection(deepAnswer) || (deepLoading ? '...' : '')}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[2000]">
      <div className="bg-white dark:bg-gray-950 w-full h-full rounded-none shadow-2xl overflow-y-auto">
        <button
          onClick={onClose}
          className="fixed top-6 right-6 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg border-2 border-red-400 p-3 z-[2100]"
          title="Close"
          type="button"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="max-w-6xl mx-auto pt-8 pb-10 px-4">
          <div className="flex items-start justify-between mb-6">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-gray-100">
              Experimental
            </h1>
          </div>

          <div className="md:hidden mb-4">
            <div className="grid grid-cols-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-1">
              <button
                type="button"
                onClick={() => setActiveTab('term')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === 'term'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10'
                }`}
              >
                Term Deep-Dive
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('inquiry')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === 'inquiry'
                    ? 'bg-gray-900 text-white'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-black/5 dark:hover:bg-white/10'
                }`}
              >
                Vedic Inquiry
              </button>
            </div>
          </div>

          <div className="md:hidden">
            {activeTab === 'term' ? TermPanel : InquiryPanel}
          </div>

          <div className={`hidden md:grid gap-6 ${maximizedPanel ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {maximizedPanel === 'term' ? TermPanel : null}
            {maximizedPanel === 'inquiry' ? InquiryPanel : null}
            {maximizedPanel === null ? (
              <>
                {TermPanel}
                {InquiryPanel}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatConversationTorana({ visible }: { visible: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none fixed top-0 right-0 hidden md:block transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
        visible ? 'translate-y-0 opacity-100' : '-translate-y-6 opacity-0'
      }`}
      style={{ zIndex: 960 }}
    >
      <style>{`
        @keyframes chat-torana-sway {
          0% { transform: rotate(0deg); }
          50% { transform: rotate(2deg); }
          100% { transform: rotate(0deg); }
        }
        @keyframes chat-torana-sway-reverse {
          0% { transform: rotate(0deg); }
          50% { transform: rotate(-2.5deg); }
          100% { transform: rotate(0deg); }
        }
        .chat-torana-sway {
          animation: chat-torana-sway 5s ease-in-out infinite;
        }
        .chat-torana-sway-alt {
          animation: chat-torana-sway-reverse 6s ease-in-out infinite 1s;
        }
        .chat-torana-sway-slow {
          animation: chat-torana-sway 7s ease-in-out infinite 0.5s;
        }
      `}</style>
      <svg
        className="h-auto w-[280px] max-w-[32vw] drop-shadow-[0_10px_24px_rgba(0,0,0,0.18)]"
        viewBox="0 0 500 380"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="chatToranaOrangeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FF7A00" />
            <stop offset="100%" stopColor="#E65100" />
          </linearGradient>
          <linearGradient id="chatToranaYellowGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFD500" />
            <stop offset="100%" stopColor="#FF9800" />
          </linearGradient>
          <linearGradient id="chatToranaGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFE57F" />
            <stop offset="50%" stopColor="#FFB300" />
            <stop offset="100%" stopColor="#F57F17" />
          </linearGradient>
          <linearGradient id="chatToranaGoldDark" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#FFCA28" />
            <stop offset="100%" stopColor="#FF8F00" />
          </linearGradient>
          <linearGradient id="chatToranaLeafGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4CAF50" />
            <stop offset="100%" stopColor="#2E7D32" />
          </linearGradient>
          <radialGradient id="chatToranaClayGrad" cx="35%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#FF8A65" />
            <stop offset="60%" stopColor="#D84315" />
            <stop offset="100%" stopColor="#8D2400" />
          </radialGradient>
          <filter id="chatToranaShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#000000" floodOpacity="0.25" />
          </filter>
          <g id="chatToranaLeaf">
            <path d="M 0,0 C 8,12 10,25 0,35 C -10,25 -8,12 0,0 Z" fill="url(#chatToranaLeafGrad)" />
            <line x1="0" y1="2" x2="0" y2="33" stroke="#1B5E20" strokeWidth="1" opacity="0.7" />
            <path d="M 0,10 Q 4,12 6,8 M 0,18 Q 5,20 7,15 M 0,26 Q 4,27 5,23" fill="none" stroke="#1B5E20" strokeWidth="0.5" opacity="0.6" />
            <path d="M 0,12 Q -4,14 -6,10 M 0,20 Q -5,22 -7,17 M 0,28 Q -4,29 -5,25" fill="none" stroke="#1B5E20" strokeWidth="0.5" opacity="0.6" />
          </g>
          <g id="chatToranaFlower">
            <rect x="-12" y="-12" width="24" height="24" rx="6" transform="rotate(0)" fill="url(#chatToranaOrangeGrad)" />
            <rect x="-12" y="-12" width="24" height="24" rx="6" transform="rotate(30)" fill="url(#chatToranaOrangeGrad)" />
            <rect x="-12" y="-12" width="24" height="24" rx="6" transform="rotate(60)" fill="url(#chatToranaOrangeGrad)" />
            <rect x="-8" y="-8" width="16" height="16" rx="4" transform="rotate(15)" fill="url(#chatToranaYellowGrad)" />
            <rect x="-8" y="-8" width="16" height="16" rx="4" transform="rotate(45)" fill="url(#chatToranaYellowGrad)" />
            <rect x="-8" y="-8" width="16" height="16" rx="4" transform="rotate(75)" fill="url(#chatToranaYellowGrad)" />
            <circle cx="0" cy="0" r="3" fill="#FFF8E1" />
          </g>
          <g id="chatToranaBell">
            <path d="M -2,-12 C -2,-18 2,-18 2,-12" fill="none" stroke="url(#chatToranaGoldDark)" strokeWidth="2" />
            <path d="M -12,12 C -12,0 -5,-12 0,-12 C 5,-12 12,0 12,12 Z" fill="url(#chatToranaGoldGrad)" />
            <rect x="-13" y="12" width="26" height="4" rx="2" fill="url(#chatToranaGoldDark)" />
            <circle cx="0" cy="18" r="4" fill="#8C6200" />
            <path d="M -9,2 Q 0,6 9,2" fill="none" stroke="#B8860B" strokeWidth="1.5" opacity="0.6" />
            <path d="M -10.5,7 Q 0,11 10.5,7" fill="none" stroke="#B8860B" strokeWidth="1.5" opacity="0.6" />
          </g>
          <g id="chatToranaEarthenPot">
            <path d="M 0,-40 L -12,6 M 0,-40 L 12,6 M 0,-40 L -6,18 M 0,-40 L 6,18" stroke="#F57F17" strokeWidth="1.5" />
            <circle cx="0" cy="-40" r="3.5" fill="#F57F17" />
            <path d="M -12,0 C -28,8 -24,28 0,30 C 24,28 28,8 12,0 Z" fill="url(#chatToranaClayGrad)" />
            <ellipse cx="0" cy="-3" rx="12" ry="3.5" fill="url(#chatToranaClayGrad)" />
            <ellipse cx="0" cy="-3.5" rx="9" ry="2" fill="#4A1000" />
            <path d="M -13,10 L -9,7 L -5,10 L -1,7 L 3,10 L 7,7 L 11,10" fill="none" stroke="#FFFFFF" strokeWidth="1.2" strokeLinejoin="round" opacity="0.85" />
            <circle cx="-9" cy="15" r="1.2" fill="#FFFFFF" opacity="0.85" />
            <circle cx="-3" cy="15" r="1.2" fill="#FFFFFF" opacity="0.85" />
            <circle cx="3" cy="15" r="1.2" fill="#FFFFFF" opacity="0.85" />
            <circle cx="9" cy="15" r="1.2" fill="#FFFFFF" opacity="0.85" />
            <use href="#chatToranaFlower" transform="translate(0, -6) scale(0.65)" />
          </g>
          <g id="chatToranaSparkle">
            <path d="M 0,-6 Q 0,0 6,0 Q 0,0 0,6 Q 0,0 -6,0 Q 0,0 0,-6 Z" fill="#FFD500" />
          </g>
        </defs>
        <g filter="url(#chatToranaShadow)">
          <path d="M 550,20 C 350,80 150,70 -20,-20" fill="none" stroke="#2E7D32" strokeWidth="2" />
          <g transform="translate(470, 41)">
            <use href="#chatToranaLeaf" transform="rotate(70)" />
            <use href="#chatToranaLeaf" transform="rotate(-10)" />
            <use href="#chatToranaFlower" transform="scale(1.1)" />
          </g>
          <g transform="translate(400, 56)">
            <use href="#chatToranaLeaf" transform="rotate(90)" />
            <use href="#chatToranaLeaf" transform="rotate(10)" />
            <use href="#chatToranaFlower" transform="scale(1)" />
          </g>
          <g transform="translate(325, 64)">
            <use href="#chatToranaLeaf" transform="rotate(100)" />
            <use href="#chatToranaLeaf" transform="rotate(20)" />
            <use href="#chatToranaFlower" transform="scale(0.9)" />
          </g>
          <g transform="translate(250, 60)">
            <use href="#chatToranaLeaf" transform="rotate(110)" />
            <use href="#chatToranaLeaf" transform="rotate(30)" />
            <use href="#chatToranaFlower" transform="scale(0.85)" />
          </g>
          <g transform="translate(175, 47)">
            <use href="#chatToranaLeaf" transform="rotate(120)" />
            <use href="#chatToranaLeaf" transform="rotate(40)" />
            <use href="#chatToranaFlower" transform="scale(0.75)" />
          </g>
          <g transform="translate(100, 25)">
            <use href="#chatToranaLeaf" transform="rotate(130)" />
            <use href="#chatToranaFlower" transform="scale(0.65)" />
          </g>
          <g transform="translate(35, -2)">
            <use href="#chatToranaLeaf" transform="rotate(140)" />
            <use href="#chatToranaFlower" transform="scale(0.5)" />
          </g>
          <g className="chat-torana-sway" style={{ transformOrigin: '400px 56px' }}>
            <line x1="400" y1="56" x2="400" y2="280" stroke="#2E7D32" strokeWidth="1.5" strokeDasharray="4 2" />
            <use href="#chatToranaFlower" transform="translate(400, 100) scale(0.85)" />
            <g transform="translate(400, 145)">
              <use href="#chatToranaLeaf" transform="rotate(50)" />
              <use href="#chatToranaLeaf" transform="rotate(-50)" />
              <use href="#chatToranaFlower" transform="scale(0.85)" />
            </g>
            <use href="#chatToranaFlower" transform="translate(400, 190) scale(0.85)" />
            <g transform="translate(400, 235)">
              <use href="#chatToranaLeaf" transform="rotate(70)" />
              <use href="#chatToranaLeaf" transform="rotate(-70)" />
              <use href="#chatToranaFlower" transform="scale(0.85)" />
            </g>
            <use href="#chatToranaBell" transform="translate(400, 280) scale(1.6)" />
            <use href="#chatToranaSparkle" transform="translate(365, 270) scale(1)" />
            <use href="#chatToranaSparkle" transform="translate(440, 290) scale(0.8)" />
          </g>
          <g className="chat-torana-sway-slow" style={{ transformOrigin: '325px 64px' }}>
            <line x1="325" y1="64" x2="325" y2="150" stroke="#2E7D32" strokeWidth="1.5" strokeDasharray="4 2" />
            <use href="#chatToranaFlower" transform="translate(325, 105) scale(0.8)" />
            <use href="#chatToranaEarthenPot" transform="translate(325, 190) scale(1.2)" />
            <use href="#chatToranaSparkle" transform="translate(290, 180) scale(0.9)" />
            <use href="#chatToranaSparkle" transform="translate(360, 210) scale(0.7)" />
          </g>
          <g className="chat-torana-sway-alt" style={{ transformOrigin: '250px 60px' }}>
            <line x1="250" y1="60" x2="250" y2="200" stroke="#2E7D32" strokeWidth="1.5" strokeDasharray="4 2" />
            <g transform="translate(250, 100)">
              <use href="#chatToranaLeaf" transform="rotate(45)" />
              <use href="#chatToranaLeaf" transform="rotate(-45)" />
              <use href="#chatToranaFlower" transform="scale(0.75)" />
            </g>
            <use href="#chatToranaFlower" transform="translate(250, 145) scale(0.75)" />
            <use href="#chatToranaBell" transform="translate(250, 200) scale(1.3)" />
          </g>
        </g>
      </svg>
    </div>
  );
}

function App() {
  const [inputValue, setInputValue] = useState('');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSolutionsOpen, setIsMobileSolutionsOpen] = useState(false);
  const [isChatSolutionsOpen, setIsChatSolutionsOpen] = useState(false);
  const [isChatMenuOpen, setIsChatMenuOpen] = useState(false);
  const [isExploreMenuOpen, setIsExploreMenuOpen] = useState(false);
  const [isExperimentalOpen, setIsExperimentalOpen] = useState(false);
  const [seenExploreFeatures, setSeenExploreFeatures] = useState(getExploreFeatureSeenState);
  const [chatViewMode, setChatViewMode] = useState<'main' | 'conversation'>('main');
  const [chatMenuExpanded, setChatMenuExpanded] = useState<{ explore: boolean; language: boolean }>({
    explore: false,
    language: false,
  });
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
  const [isFeedbackSuccess, setIsFeedbackSuccess] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const {
    hitsCount: count,
    isHitsReady: isTokenCountReady,
    hasPendingStripePayment,
    pendingStripePaymentKind,
    subscriptionName,
    isPaidPlan,
  } = useUserHits();
  const { t, i18n } = useTranslation();
  const { resolvedTheme, setTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';
  const aiSuiteFallback = (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
    </div>
  );


  const location = useLocation();
  useClarityTags();
  const dropdownRef = useRef<any>(null);
  const chatMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const chatMenuRef = useRef<HTMLElement | null>(null);
  const exploreMenuRef = useRef<HTMLDivElement | null>(null);
  const solutionsMenuRef = useRef<HTMLDivElement | null>(null);
  const headerBarRef = useRef<HTMLElement | null>(null);
  const themeTransitioningRef = useRef(false);
  const solutionsHoverOpenTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const solutionsHoverCloseTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  // Sliding header state — desktop only, chat conversation page only
  const [isHeaderSlid, setIsHeaderSlid] = useState(false); // true = header is slid up (hidden)
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const slideCloseTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const slideAutoHideTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const slideOpenTimeoutRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const SLIDE_OPEN_INTENT_DELAY_MS = 175;
  const localizedPath = useLocalizedPath();
  const isChatRoute = /\/chat$/.test(location.pathname);
  const isAdLandingSignUpRoute =
    /\/sign-up$/.test(location.pathname) &&
    isValidAdMode(new URLSearchParams(location.search).get('mode'));
  const isHomePage =
    location.pathname === '/' ||
    /^\/[a-z]{2}\/?$/.test(location.pathname);
  const showChatConversationHeaderNav = Boolean(user && isChatRoute && chatViewMode === 'conversation');
  const pendingStripePaymentLabel =
    pendingStripePaymentKind === 'subscription'
      ? t('nav.payment_pending_subscription', { defaultValue: 'Subscription payment in progress' })
      : t('nav.payment_pending_topup', { defaultValue: 'Top-up payment in progress' });
  const userPlanName = isPaidPlan ? formatSubscriptionNameForDisplay(subscriptionName) : '';
  const accountMenuLabel = [
    t('nav.account_menu', { defaultValue: 'Account menu' }),
    userPlanName || null,
    hasPendingStripePayment ? pendingStripePaymentLabel : null,
  ].filter(Boolean).join(', ');

  const clearSolutionsHoverTimers = () => {
    if (solutionsHoverOpenTimeoutRef.current) {
      window.clearTimeout(solutionsHoverOpenTimeoutRef.current);
      solutionsHoverOpenTimeoutRef.current = null;
    }
    if (solutionsHoverCloseTimeoutRef.current) {
      window.clearTimeout(solutionsHoverCloseTimeoutRef.current);
      solutionsHoverCloseTimeoutRef.current = null;
    }
  };

  const scheduleOpenSolutions = () => {
    if (isSolutionsOpen) return;
    if (solutionsHoverCloseTimeoutRef.current) {
      window.clearTimeout(solutionsHoverCloseTimeoutRef.current);
      solutionsHoverCloseTimeoutRef.current = null;
    }
    if (solutionsHoverOpenTimeoutRef.current) window.clearTimeout(solutionsHoverOpenTimeoutRef.current);
    solutionsHoverOpenTimeoutRef.current = window.setTimeout(() => {
      setIsSolutionsOpen(true);
    }, 100);
  };

  const scheduleCloseSolutions = () => {
    if (solutionsHoverOpenTimeoutRef.current) {
      window.clearTimeout(solutionsHoverOpenTimeoutRef.current);
      solutionsHoverOpenTimeoutRef.current = null;
    }
    if (solutionsHoverCloseTimeoutRef.current) window.clearTimeout(solutionsHoverCloseTimeoutRef.current);
    solutionsHoverCloseTimeoutRef.current = window.setTimeout(() => {
      setIsSolutionsOpen(false);
    }, 340);
  };

  const cancelCloseSolutions = () => {
    if (solutionsHoverCloseTimeoutRef.current) {
      window.clearTimeout(solutionsHoverCloseTimeoutRef.current);
      solutionsHoverCloseTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearSolutionsHoverTimers();
  }, []);

  const solutionsMenuItems = [
    {
      to: '/for-education',
      titleKey: 'nav.solutions_education',
      descKey: 'nav.solutions_education_desc',
      defaultTitle: 'Education',
      defaultDesc: 'University programs & research tools',
      delay: '20ms',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M22 10L12 5L2 10L12 15L22 10Z" />
          <path d="M6 12V16C6 16 8 19 12 19C16 19 18 16 18 16V12" />
        </svg>
      ),
    },
    {
      to: '/for-organisation',
      titleKey: 'nav.solutions_organisations',
      descKey: 'nav.solutions_organisations_desc',
      defaultTitle: 'Organisations',
      defaultDesc: 'Temples, ashrams & cultural bodies',
      delay: '50ms',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" />
          <path d="M14 2V8H20" />
          <path d="M8 13H16" />
          <path d="M8 17H13" />
        </svg>
      ),
    },
    {
      to: '/for-government',
      titleKey: 'nav.solutions_government',
      descKey: 'nav.solutions_government_desc',
      defaultTitle: 'Government',
      defaultDesc: 'Heritage digitisation & public initiatives',
      delay: '80ms',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 21H21" />
          <path d="M5 21V8L12 3L19 8V21" />
          <path d="M9 21V12H15V21" />
        </svg>
      ),
    },
    {
      to: '/enterprise',
      titleKey: 'nav.solutions_enterprise',
      descKey: 'nav.solutions_enterprise_desc',
      defaultTitle: 'Enterprise',
      defaultDesc: 'Custom plans for institutions',
      delay: '110ms',
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
          <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
          <path d="M12 12v.01" />
        </svg>
      ),
    },
  ] as const;

  const SolutionsDropdown = () => (
    <div className="relative" ref={solutionsMenuRef}>
      <button
        type="button"
        onMouseEnter={scheduleOpenSolutions}
        onMouseLeave={scheduleCloseSolutions}
        onClick={() => {
          clearSolutionsHoverTimers();
          setIsDropdownOpen(false);
          setIsSolutionsOpen((v) => !v);
        }}
        className="sl-topnav-link sl-solutions-trigger hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg inline-flex items-center gap-1"
        aria-haspopup="menu"
        aria-expanded={isSolutionsOpen}
      >
        <span>{t('nav.solutions', { defaultValue: 'Solutions' })}</span>
        <svg
          className={`h-[10px] w-[10px] transition-transform duration-300 ${isSolutionsOpen ? 'rotate-180 opacity-60' : 'opacity-40'}`}
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden="true"
        >
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isSolutionsOpen ? (
        <>
          <button
            type="button"
            className="fixed inset-0 z-[997] cursor-default bg-transparent"
            aria-label="Close Solutions menu"
            onClick={() => {
              clearSolutionsHoverTimers();
              setIsSolutionsOpen(false);
            }}
          />
          <div
            onMouseEnter={cancelCloseSolutions}
            onMouseLeave={scheduleCloseSolutions}
            className="sl-solutions-menu-panel absolute left-1/2 top-full z-[1000] mt-[12px] w-[320px] -translate-x-1/2 p-[6px] backdrop-blur-md solutions-menu-panel"
            style={{
              background: isDarkMode ? 'rgba(15,23,42,0.98)' : 'rgba(255,255,255,0.98)',
              border: isDarkMode ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.08)',
              borderRadius: 14,
              boxShadow: '0 24px 80px -16px rgba(0,0,0,0.14), 0 0 1px rgba(0,0,0,0.12)',
            }}
            role="menu"
          >
            {solutionsMenuItems.map((item) => (
              <LocalizedLink
                key={item.to}
                to={item.to}
                onClick={() => {
                  clearSolutionsHoverTimers();
                  setIsSolutionsOpen(false);
                  setIsDropdownOpen(false);
                  setIsMobileMenuOpen(false);
                }}
                className="sl-solutions-menu-item group flex items-center gap-[14px] px-[14px] py-[12px] rounded-[10px] hover:bg-[#f5f5f7] dark:hover:bg-white/10 transition solutions-menu-item text-slate-700 dark:text-slate-200"
                style={{
                  animationDelay: item.delay,
                }}
                role="menuitem"
              >
                <span className="shrink-0 opacity-[0.55] group-hover:opacity-[0.85] transition-opacity">
                  {item.icon}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className="block text-[13.5px] font-medium tracking-[-0.01em] text-slate-900 dark:text-slate-50 leading-tight"
                    style={{ fontFamily: 'var(--font-sans, var(--font-body, inherit))' as any }}
                  >
                    {t(item.titleKey, { defaultValue: item.defaultTitle })}
                  </span>
                  <span
                    className="sl-solutions-menu-desc mt-[2px] block text-[12px] font-normal text-slate-500 dark:text-slate-400 leading-snug"
                    style={{ fontFamily: 'var(--font-sans, var(--font-body, inherit))' as any }}
                  >
                    {t(item.descKey, { defaultValue: item.defaultDesc })}
                  </span>
                </span>
                <span className="shrink-0 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </LocalizedLink>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );

  // ---------- Sliding header: desktop-only, chat conversation page only ----------
  // Must be declared before any useEffect that references it
  const shouldSlideHeader = isChatRoute && chatViewMode === 'conversation';

  useEffect(() => {
    const headerEl = headerBarRef.current;
    if (!headerEl) return;

    const root = document.documentElement;
    const setHeaderHeightVar = () => {
      // When sliding header is active and hidden, the slide-state effect manages --app-header-h
      if (shouldSlideHeader && isHeaderSlid && !isHeaderHovered) return;
      const h = headerEl.getBoundingClientRect().height;
      root.style.setProperty('--app-header-h', `${h}px`);
    };

    setHeaderHeightVar();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => setHeaderHeightVar());
      ro.observe(headerEl);
    }

    window.addEventListener('resize', setHeaderHeightVar);
    return () => {
      window.removeEventListener('resize', setHeaderHeightVar);
      ro?.disconnect();
    };
  }, [shouldSlideHeader, isHeaderSlid, isHeaderHovered]);

  useEffect(() => {
    if (shouldSlideHeader) {
      setHeaderScrolled(false);
      const onChatScroll = (e: Event) => {
        const detail = (e as CustomEvent)?.detail;
        setHeaderScrolled(Boolean(detail?.scrolled));
      };
      window.addEventListener('sarvalok_chat_scroll', onChatScroll as any);
      return () => window.removeEventListener('sarvalok_chat_scroll', onChatScroll as any);
    }

    const onScroll = () => setHeaderScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [shouldSlideHeader]);
  useEffect(() => {
    const handler = (e: Event) => {
      const mode = (e as CustomEvent)?.detail?.mode;
      if (mode === 'main' || mode === 'conversation') {
        setChatViewMode(mode);
      }
    };
    window.addEventListener('sarvalok_chat_view_mode', handler);
    return () => window.removeEventListener('sarvalok_chat_view_mode', handler);
  }, []);

  useEffect(() => {
    if (!isChatRoute) setChatViewMode('main');
  }, [isChatRoute]);

  useEffect(() => {
    if (!isChatRoute) return;
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1') {
      setChatViewMode('main');
    }
  }, [isChatRoute, location.search]);

  // Auto-hide header 2.5s after entering conversation mode
  useEffect(() => {
    if (slideAutoHideTimeoutRef.current) {
      clearTimeout(slideAutoHideTimeoutRef.current);
      slideAutoHideTimeoutRef.current = null;
    }
    if (!shouldSlideHeader) {
      // Reset when leaving conversation mode
      if (slideOpenTimeoutRef.current) {
        clearTimeout(slideOpenTimeoutRef.current);
        slideOpenTimeoutRef.current = null;
      }
      if (slideCloseTimeoutRef.current) {
        clearTimeout(slideCloseTimeoutRef.current);
        slideCloseTimeoutRef.current = null;
      }
      setIsHeaderSlid(false);
      setIsHeaderHovered(false);
      return;
    }
    // Desktop-only: check min width
    if (window.innerWidth < 768) return;
    slideAutoHideTimeoutRef.current = setTimeout(() => {
      setIsHeaderSlid(true);
    }, 2500);
    return () => {
      if (slideAutoHideTimeoutRef.current) {
        clearTimeout(slideAutoHideTimeoutRef.current);
        slideAutoHideTimeoutRef.current = null;
      }
      if (slideOpenTimeoutRef.current) {
        clearTimeout(slideOpenTimeoutRef.current);
        slideOpenTimeoutRef.current = null;
      }
    };
  }, [shouldSlideHeader]);

  // Update --app-header-h when header slides (for ChatPage offset)
  useEffect(() => {
    if (!shouldSlideHeader) return;
    const root = document.documentElement;
    if (isHeaderSlid && !isHeaderHovered) {
      // Header is hidden — only pull tab visible (~28px)
      root.style.setProperty('--app-header-h', '28px');
    } else {
      // Header is visible — measure actual height
      const headerEl = headerBarRef.current;
      if (headerEl) {
        const h = headerEl.getBoundingClientRect().height;
        root.style.setProperty('--app-header-h', `${h}px`);
      }
    }
  }, [isHeaderSlid, isHeaderHovered, shouldSlideHeader]);

  // Dispatch resize event when header slide state changes so ChatPage recalculates offset
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [isHeaderSlid, isHeaderHovered]);

  const toggleChatMenuSection = (id: 'explore' | 'language') => {
    setChatMenuExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const hasUnseenExploreFeature = !seenExploreFeatures.rigveda || !seenExploreFeatures.chant;

  const markExploreFeatureSeen = (feature: ExploreFeatureKey) => {
    try {
      window.localStorage.setItem(EXPLORE_FEATURE_STORAGE_KEYS[feature], '1');
    } catch {}
    setSeenExploreFeatures(prev => ({ ...prev, [feature]: true }));
  };

  const getActiveThreadId = () => {
    try {
      const url = new URL(window.location.href);
      const fromQuery = url.searchParams.get('id');
      if (fromQuery) return fromQuery;
    } catch {}
    try {
      return localStorage.getItem('thread_id') || undefined;
    } catch {
      return undefined;
    }
  };

  const sanitizeFeedbackMessage = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    let out = '';
    for (let i = 0; i < trimmed.length; i += 1) {
      const code = trimmed.charCodeAt(i);
      const isControl = (code >= 0 && code <= 31) || code === 127;
      if (isControl) continue;
      out += trimmed[i];
    }
    return out.trim();
  };

  const handleFeedbackSubmit = async (type: FeedbackType, message: string) => {
    if (isFeedbackSubmitting) return;
    const cleaned = sanitizeFeedbackMessage(message);
    if (!cleaned || cleaned.length > 2000) {
      setFeedbackError('Please enter a valid feedback message.');
      return;
    }

    setIsFeedbackSubmitting(true);
    setFeedbackError(null);
    setIsFeedbackSuccess(false);
    try {
      const threadId = getActiveThreadId();
      await submitFeedback({
        type,
        message: cleaned,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
        thread_id: threadId,
      });
      setIsFeedbackSuccess(true);
      window.setTimeout(() => {
        setIsFeedbackOpen(false);
        setIsFeedbackSuccess(false);
        setFeedbackError(null);
      }, 2000);
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to submit feedback. Please try again.';
      setFeedbackError(String(msg));
    } finally {
      setIsFeedbackSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isExperimentalOpen) return;
    const original = document.body.style.overflow;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsExperimentalOpen(false);
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = original;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isExperimentalOpen]);
  
  // Initialize analytics and enable automatic page tracking
  const analyticsStatus = useAnalytics();

  // Toggle theme and save to localStorage
  const toggleTheme = (event: React.MouseEvent<HTMLButtonElement>) => {
    const next = isDarkMode ? 'light' : 'dark';
    const applyNextTheme = () => {
      flushSync(() => {
        setTheme(next);
      });
      document.documentElement.classList.toggle('dark', next === 'dark');
    };

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const doc = document as DocumentWithViewTransition;

    if (themeTransitioningRef.current) {
      return;
    }

    if (!doc.startViewTransition || prefersReducedMotion) {
      applyNextTheme();
    } else {
      themeTransitioningRef.current = true;

      const x = event.clientX;
      const y = event.clientY;
      const endRadius = Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y)
      );
      const transition = doc.startViewTransition(applyNextTheme);

      transition.ready
        .then(() => {
          requestAnimationFrame(() => {
            try {
              document.documentElement.animate(
                {
                  clipPath: [
                    `circle(0px at ${x}px ${y}px)`,
                    `circle(${endRadius}px at ${x}px ${y}px)`,
                  ],
                },
                {
                  duration: 550,
                  easing: 'ease-in-out',
                  pseudoElement: '::view-transition-new(root)',
                } as KeyframeAnimationOptions & { pseudoElement: string }
              );
            } catch {
              themeTransitioningRef.current = false;
            }
          });
        })
        .catch(() => {
          themeTransitioningRef.current = false;
        });

      transition.finished
        .catch(() => undefined)
        .finally(() => {
          themeTransitioningRef.current = false;
        });
    }

    trackEvent('theme_toggle', {
      theme: next,
      custom_parameters: {
        user_preference: next,
        timestamp: new Date().toISOString()
      }
    });
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    setIsSolutionsOpen(false);
    setIsMobileSolutionsOpen(false);
  }, [location.pathname]);

  // Initialize analytics
  useEffect(() => {
    analytics.initialize();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsDropdownOpen(false);
      }
      if (exploreMenuRef.current && !exploreMenuRef.current.contains(target)) {
        setIsExploreMenuOpen(false);
      }
      if (solutionsMenuRef.current && !solutionsMenuRef.current.contains(target)) {
        setIsSolutionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isExploreMenuOpen, isSolutionsOpen]);

  useEffect(() => {
    if (!isSolutionsOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsSolutionsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isSolutionsOpen]);

  useEffect(() => {
    if (!isChatRoute) {
      setIsChatMenuOpen(false);
      setIsExploreMenuOpen(false);
    }
  }, [isChatRoute]);

  useEffect(() => {
    if (!isChatMenuOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsChatMenuOpen(false);
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      if (!isChatMenuOpen) return;
      const menuEl = chatMenuRef.current;
      const buttonEl = chatMenuButtonRef.current;
      const target = e.target as Node;
      if (menuEl && !menuEl.contains(target) && buttonEl && !buttonEl.contains(target)) {
        setIsChatMenuOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isChatMenuOpen]);

  useEffect(() => {
    if (isChatMenuOpen) return;
    if (!isChatRoute) return;
    chatMenuButtonRef.current?.focus();
  }, [isChatMenuOpen, isChatRoute]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser && currentUser.emailVerified) {
        setUser(currentUser);
        try {
          cacheAuthUser(currentUser);
          identifyClarityUser(currentUser.uid);
          
          // Fetch user data and set analytics properties
          const userDocRef = doc(db, 'users', currentUser.uid);
          let userDoc = await getDoc(userDocRef);
          let userData = userDoc.exists() ? (userDoc.data() || {}) : null;

          if (!userDoc.exists() || needsUserDocRepair(userData)) {
            try {
              userData = await ensureAuthUserDoc(currentUser, {
                provider: providerIdForUser(currentUser),
                source: userDoc.exists() ? 'app_auth_state_partial_doc' : 'app_auth_state_missing_doc',
                includeDeviceInfo: false,
              });
              userDoc = await getDoc(userDocRef);
              userData = userDoc.exists() ? (userDoc.data() || userData) : userData;
            } catch (repairError) {
              console.error("Error repairing user document during auth state check:", repairError);
              if (isEnsureUserBlockedError(repairError)) {
                clearCachedAuthUser();
                setUser(null);
                try {
                  await signOut(auth);
                } catch {}
                console.warn("Signed out blocked Sarvalok account:", ensureUserErrorMessage(repairError));
                return;
              }
            }
          }
          
          if (userData) {
            const plan = userData.plan || 'free';
            
            // Set user properties for analytics
            setUserProperties({
              user_id: currentUser.uid,
              user_plan: plan,
              user_email: currentUser.email || undefined,
              user_verified: currentUser.emailVerified,
              custom_parameters: {
                registration_date: userData.createdAt || new Date().toISOString(),
                last_login: new Date().toISOString()
              }
            });
            
            // Track user authentication
            trackEvent('login', {
              method: 'firebase',
              user_plan: plan,
              custom_parameters: {
                user_verified: currentUser.emailVerified
              }
            });
          }
        } catch (error) {
          console.error("Error saving user to localStorage:", error);
        }
      } else {
        setUser(null);
        try {
          localStorage.removeItem('user');
          
          // Clear user properties when logged out
          setUserProperties({
            user_id: undefined,
            user_plan: 'free',
            user_email: undefined,
            user_verified: false
          });
        } catch (error) {
          console.error("Error removing user from localStorage:", error);
        }
      }
      setLoading(false);
    });

    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUser((prev) => prev ?? parsedUser);
      }
    } catch (error) {
      console.error("Error reading from localStorage:", error);
    }

    return () => unsubscribe();
  }, []);

  const handleLogout = () => {
    // Track logout event
    trackEvent('logout', {
      method: 'manual',
      custom_parameters: {
        timestamp: new Date().toISOString()
      }
    });
    
    signOut(auth)
      .then(() => {
        try {
          sessionStorage.setItem('chat_force_new_session', '1');
        } catch {}
        try {
          localStorage.removeItem('user');
        } catch {}
        window.location.href = "/";
      })
      .catch((error) => {
        console.error('Error logging out:', error);
      });
  };

  const toggleDropdown = () => {
    setIsSolutionsOpen(false);
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setIsDropdownOpen(false);
    setIsSolutionsOpen(false);
    setIsMobileSolutionsOpen(false);
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const isIndianLocaleUser = typeof window !== 'undefined' && Boolean((window as any).__isIndianLocale__);

  const handleAiStudioRedirect = async () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setIsChatMenuOpen(false);

    const apiBaseUrl = import.meta.env.VITE_APP_SERVER || '';

    try {
      const headers = await getSecureHeaders();
      const res = await fetch(`${apiBaseUrl}/api/weave/sso-token`, {
        method: 'POST',
        headers,
        cache: 'no-store',
      });

      if (!res.ok) throw new Error(`Failed to create SSO token (${res.status})`);
      const data = await res.json();
      const token = data?.token;
      if (!token) throw new Error('Missing SSO token');

      window.location.assign(
        `https://ai.sarvalok.com/sso/sarvalok?token=${encodeURIComponent(String(token))}`
      );
    } catch (err) {
      window.location.assign('https://ai.sarvalok.com/login?callbackUrl=%2Fnewchat');
    }
  };

  const handleConversationBackClick = () => {
    setIsExploreMenuOpen(false);
    setIsChatMenuOpen(false);
    window.dispatchEvent(new Event('sarvalok_consult_main_chat'));
  };

  // Global organization and website schemas using @graph
  const globalSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "name": "Sarvalok",
        "url": "https://sarvalok.com",
        "logo": "https://sarvalok.com/logo.png",
        "description": "Making authentic Dharma knowledge universally accessible through AI-powered platform",
        "foundingDate": "2024",
        "sameAs": [
          "https://twitter.com/sarvalok",
          "https://linkedin.com/company/sarvalok"
        ]
      },
      {
        "@type": "WebSite",
        "url": "https://sarvalok.com",
        "name": "Sarvalok",
        "description": "The essential AI tool for Indology, research, and spiritual study. Sarvalok offers source-grounded analysis of the Vedas, Upanishads, and Sanātana Dharma texts.",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://sarvalok.com/chat?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      }
    ]
  };

  return (
    <>
      <SplashGate appLoading={loading} pathname={location.pathname} />
      {loading ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-black"></div>
        </div>
      ) : (
        <>
          <Helmet>
            {/* Schema-only global metadata. Page canonicals/hreflang live in route-level <SEO />. */}
            <script type="application/ld+json">{JSON.stringify(globalSchema)}</script>
          </Helmet>
          <div className="min-h-screen">
          {isExperimentalOpen ? <ExperimentalOverlay onClose={() => setIsExperimentalOpen(false)} /> : null}
          {/* ---------- Sliding header wrapper (desktop, conversation only) ---------- */}
	          {!isAdLandingSignUpRoute && (
	          <>
	          <div
        className={`fixed top-0 left-0 w-full z-50 flex flex-col${shouldSlideHeader ? ' drop-shadow-sm' : ''}`}
        style={{
          zIndex: 999,
          transform: shouldSlideHeader && isHeaderSlid && !isHeaderHovered
            ? 'translateY(calc(-100% + 28px))'
            : 'translateY(0)',
          // Asymmetric transitions from sample: fast slide down, bouncy slide up
          transition: !shouldSlideHeader
            ? 'none'
            : (isHeaderSlid && !isHeaderHovered)
              ? 'transform 0.8s cubic-bezier(0.5, 1.35, 0.5, 1)'  // Bouncy slide up (closing)
              : 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)',   // Fast slide down (opening)
        }}
        onMouseEnter={() => {
          if (!shouldSlideHeader) return;
          // Cancel auto-hide if mouse enters before 2.5s fires
          if (slideAutoHideTimeoutRef.current) {
            clearTimeout(slideAutoHideTimeoutRef.current);
            slideAutoHideTimeoutRef.current = null;
          }
          // Cancel close-delay if returning before 600ms
	          if (slideCloseTimeoutRef.current) {
	            clearTimeout(slideCloseTimeoutRef.current);
	            slideCloseTimeoutRef.current = null;
	          }
	          if (slideOpenTimeoutRef.current) {
	            clearTimeout(slideOpenTimeoutRef.current);
	            slideOpenTimeoutRef.current = null;
	          }
	          if (isHeaderSlid && !isHeaderHovered) {
	            slideOpenTimeoutRef.current = setTimeout(() => {
	              slideOpenTimeoutRef.current = null;
	              setIsHeaderHovered(true);
	            }, SLIDE_OPEN_INTENT_DELAY_MS);
	          }
	        }}
	        onMouseLeave={() => {
	          if (!shouldSlideHeader) return;
	          if (slideOpenTimeoutRef.current) {
	            clearTimeout(slideOpenTimeoutRef.current);
	            slideOpenTimeoutRef.current = null;
	          }
	          if (!isHeaderSlid) {
	            // Initial auto-hide hasn't fired yet; restart the 2.5s timer
	            if (slideAutoHideTimeoutRef.current) clearTimeout(slideAutoHideTimeoutRef.current);
            slideAutoHideTimeoutRef.current = setTimeout(() => {
              setIsHeaderSlid(true);
            }, 2500);
            return;
          }
          // Already slid: close after 600ms delay (from sample: CLOSE_DELAY_MS)
          slideCloseTimeoutRef.current = setTimeout(() => {
            setIsHeaderHovered(false);
          }, 600);
        }}
      >
      <header ref={headerBarRef} className={`w-full transition-all duration-200 ${
        shouldSlideHeader && isHeaderSlid && isHeaderHovered
          ? 'bg-white/70 backdrop-blur-md backdrop-saturate-150 border-b border-black/10'
          : headerScrolled
            ? 'bg-white/70 backdrop-blur-md backdrop-saturate-150 border-b border-black/10'
            : 'bg-white border-b border-gray-200'
      }`}>
        {/* Previous header padding: py-2.5 (reverted to slim ~56px header) */}
        <div className="flex items-center justify-between px-4 pt-[4px] pb-[13px] max-w-1xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="flex items-center gap-1 min-w-0 shrink-0">
              {/* Logo and Brand */}
              {/* Previous logo link spacing: p-2 mb-2 (reverted to slim ~56px header) */}
              <LocalizedLink to={user ? "/chat?new=1" : "/"} className="p-2 hover:bg-black/5 rounded-md transition-colors">
                <img src="/logo.png" alt="Sarvalok" className="w-12 no-dark" />
              </LocalizedLink>
              {/* Previous logo text style (for quick rollback): uppercase, 20px, fontWeight 800, marginLeft -6px */}
              {/* <LocalizedLink to={user ? "/chat?new=1" : "/"} className="font-semibold text-[20px]" style={{ fontWeight: 800, marginLeft: "-6px" }}>
                SARVALOK
              </LocalizedLink> */}
              <LocalizedLink
                to={user ? "/chat?new=1" : "/"}
                className={`text-[22px] sm:text-[26px] leading-none whitespace-nowrap translate-y-[5px] ${showChatConversationHeaderNav ? 'hidden sm:inline-block' : 'inline-block'}`}
                style={{ fontFamily: "'General Sans', 'Inter', 'Segoe UI', -apple-system, sans-serif", fontWeight: 600, marginLeft: "-6px" }}
              >
                Sarvalok
              </LocalizedLink>
            </div>
            <button
              onClick={toggleTheme}
              className={`relative w-9 h-9 rounded-lg border transition-all duration-300 hover:scale-105 ${
                isDarkMode
                  ? 'bg-slate-700 border-slate-600 hover:bg-slate-600'
                  : 'bg-white border-gray-300 hover:bg-gray-50'
              }`}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <Moon className="w-5 h-5 text-blue-400 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              ) : (
                <Sun className="w-5 h-5 text-amber-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              )}
            </button>
            {showChatConversationHeaderNav ? (
              <div className="flex items-center gap-2 shrink-0">
                <LocalizedLink
                  to="/chat?new=1"
                  onClick={handleConversationBackClick}
                  className="inline-flex items-center bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition border border-gray-200 dark:border-gray-700 leading-none"
                >
                  <span>{t('nav.back', { defaultValue: 'Back' })}</span>
                </LocalizedLink>
                <LocalizedLink
                  to="/"
                  className="inline-flex items-center bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition border border-gray-200 dark:border-gray-700 leading-none"
                >
                  <span>{t('nav.home', { defaultValue: 'Home' })}</span>
                </LocalizedLink>
              </div>
            ) : null}
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {isChatRoute ? (
              <>
                {showChatConversationHeaderNav ? (
                  <div className="hidden md:flex items-center gap-2">
                    <LocalizedLink
                      to="/chat?new=1"
                      onClick={handleConversationBackClick}
                      className="inline-flex items-center bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition border border-gray-200 dark:border-gray-700 leading-none"
                    >
                      {t('chat.new_chat', { defaultValue: 'New Chat' })}
                    </LocalizedLink>
                  </div>
                ) : null}

                <div className="relative hidden md:inline-flex" ref={exploreMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsExploreMenuOpen((v) => !v)}
                  className="inline-flex items-center bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition border border-gray-200 dark:border-gray-700 leading-none"
                  aria-haspopup="menu"
                  aria-expanded={isExploreMenuOpen}
                >
                  <span>{t('nav.explore', { defaultValue: 'Explore' })}</span>
                  {hasUnseenExploreFeature ? <ExploreFeatureDot className="ml-2" /> : null}
                </button>
                  {isExploreMenuOpen && (
                    <div
                      className="sl-explore-menu-panel absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
                      role="menu"
                    >
                      <a
                        href="https://rigveda.sarvalok.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          markExploreFeatureSeen('rigveda');
                          setIsExploreMenuOpen(false);
                        }}
                        className="sl-explore-menu-item flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                        role="menuitem"
                      >
                        <span>{t('nav.rig_veda', { defaultValue: 'Rig Veda' })}</span>
                        {!seenExploreFeatures.rigveda ? <ExploreFeatureDot /> : null}
                      </a>
                      <a
                        href="https://chant.sarvalok.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          markExploreFeatureSeen('chant');
                          setIsExploreMenuOpen(false);
                        }}
                        className="sl-explore-menu-item flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                        role="menuitem"
                      >
                        <span>{t('nav.chant', { defaultValue: 'Chant' })}</span>
                        {!seenExploreFeatures.chant ? <ExploreFeatureDot /> : null}
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          setIsExploreMenuOpen(false);
                          setIsExperimentalOpen(true);
                        }}
                        className="sl-explore-menu-item block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                        role="menuitem"
                      >
                        {t('nav.experimental', { defaultValue: 'Experimental' })}
                      </button>
                    </div>
                  )}
                </div>

                <LocalizedLink
                  to="/global-risk"
                  onClick={() => setIsExploreMenuOpen(false)}
                  className="hidden md:inline-flex items-center bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition border border-gray-200 dark:border-gray-700 leading-none"
                >
                  {t('nav.global_risk_radar', { defaultValue: 'Global Risk Radar' })}
                </LocalizedLink>

                {user ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsFeedbackOpen(true);
                      setFeedbackError(null);
                      setIsFeedbackSuccess(false);
                    }}
                    className="hidden md:inline-flex items-center bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition border border-gray-200 dark:border-gray-700 leading-none"
                  >
                    {t('nav.feedback', { defaultValue: 'Feedback' })}
                  </button>
                ) : null}

                {showChatConversationHeaderNav ? (
                  <ShareWithFriends
                    sourceSurface="header"
                    variant="header"
                  />
                ) : null}

                <button
                  ref={chatMenuButtonRef}
                  onClick={() => setIsChatMenuOpen((v) => !v)}
                  className="p-2 focus:outline-none rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                  aria-haspopup="dialog"
                  aria-expanded={isChatMenuOpen}
                  aria-label="Open menu"
                  type="button"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d={isChatMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                    />
                  </svg>
                </button>
              </>
            ) : (
              <div className="lg:hidden">
                <button onClick={toggleMobileMenu} className="p-2 focus:outline-none" type="button">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {!isChatRoute && <div className="hidden lg:flex items-center gap-4 translate-y-[5px]">
            {user ? (
              <div className="relative flex items-center" ref={dropdownRef}>
                <div style={{ fontWeight: 500, fontSize: "15px" }} className="flex items-center space-x-6 text-sm font-semibold text-black dark:text-white">
                  <LocalizedLink to="/features" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="sl-topnav-link hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.features')}</LocalizedLink>
                  <SolutionsDropdown />
                  <LocalizedLink to="/for-investor" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsSolutionsOpen(false) }} className="sl-topnav-link hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.for_investor')}</LocalizedLink>
                  <LocalizedLink to="/pricing" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="sl-topnav-link hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.pricing')}</LocalizedLink>
                  {/* AI Studio: hidden for Indian/Razorpay users — they don't get Weave access */}
                  {!isIndianLocaleUser && (
                    <LocalizedLink
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleAiStudioRedirect();
                      }}
                      className="sl-topnav-link hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg"
                    >
                      {t('nav.ai_studio', { defaultValue: 'AI Studio' })}
                    </LocalizedLink>
                  )}
                  <LocalizedLink
                    to="/chat"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
                  >
                    {t('nav.consult_sarvalok')}
                  </LocalizedLink>
                </div>
                <div className="ml-2"><LanguageSwitcher /></div>
                <button
                  onClick={toggleDropdown}
                  className="sv-premium-avatar-control relative p-2 hover:bg-black/5 rounded-full transition-colors ml-4"
                  aria-label={accountMenuLabel}
                >
                  <PremiumAvatarFace user={user} isPaidPlan={isPaidPlan} />
                  {hasPendingStripePayment ? (
                    <PaymentPendingDot label={pendingStripePaymentLabel} className="absolute right-1 top-1 ring-2 ring-white dark:ring-gray-950" />
                  ) : null}
                </button>
                {isDropdownOpen && (
                  <div className="sl-profile-menu-panel absolute right-0 top-12 mt-2 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
                    <div className="sl-profile-menu-section px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                      <p className="text-sm font-medium truncate text-gray-900 dark:text-white">{user?.displayName?.trim() ? user.displayName : user?.email}</p>
                      {userPlanName ? (
                        <p className="mt-0.5 text-xs font-medium truncate text-[#8B6F47] dark:text-[#D4B575]">{userPlanName}</p>
                      ) : null}
                    </div>
                    <div className="sl-profile-menu-section px-4 py-2 border-b border-gray-200 dark:border-gray-600">
                      <p className="text-sm font-medium truncate text-gray-900 dark:text-white">
                        <span className="inline-flex items-center gap-1">
                          {t('nav.tokens_left', { count: isTokenCountReady ? count : '…' })}
                          {!isTokenCountReady && <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" />}
                        </span>
                      </p>
                    </div>
                    <LocalizedLink onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} to="/settings" className="sl-profile-menu-item block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <span className="flex items-center justify-between gap-2">
                        <span className="flex items-center">
                          <Settings className="w-4 h-4 mr-2" />
                          {t('nav.settings')}
                        </span>
                        {hasPendingStripePayment ? <PaymentPendingDot label={pendingStripePaymentLabel} /> : null}
                      </span>
                    </LocalizedLink>
                    <LocalizedLink onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} to="/pricing" className="sl-profile-menu-item block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <span className="flex items-center">
                        <Ticket className="w-4 h-4 mr-2" />
                        {t('nav.upgrade_plan')}
                      </span>
                    </LocalizedLink>
                    <button
                      onClick={handleLogout}
                      className="sl-profile-menu-item block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <span className="flex items-center">
                        <AccountCircle className="w-4 h-4 mr-2" />
                        {t('nav.logout')}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontWeight: 500, fontSize: "15px" }} className="flex items-center space-x-6 text-sm font-semibold text-black dark:text-white">
                <LocalizedLink to="/features" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="sl-topnav-link hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.features')}</LocalizedLink>
                <SolutionsDropdown />
                <LocalizedLink to="/for-investor" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsSolutionsOpen(false) }} className="sl-topnav-link hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.for_investor')}</LocalizedLink>
                <LocalizedLink to="/pricing" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="sl-topnav-link hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.pricing')}</LocalizedLink>
                <LocalizedLink
                  to="/login"
                  className="sl-topnav-login bg-gray-100 dark:bg-gray-800 text-black dark:text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                >
                  {t('nav.login')}
                </LocalizedLink>
                <LocalizedLink
                  to="/sign-up"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
                >
                  {isHomePage ? t('nav.sign_up', { defaultValue: 'Sign Up' }) : t('nav.free_trial')}
                </LocalizedLink>
                <div className="ml-2"><LanguageSwitcher /></div>
              </div>
            )}
          </div>}
        </div>

        {!isChatRoute && isMobileMenuOpen && (
          <div className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-xl ring-1 ring-black/5 dark:ring-white/10">
            {user ? (
              <div className="px-4 py-2">
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium truncate text-black dark:text-white">{user?.displayName?.trim() ? user.displayName : user?.email}</p>
                    <p className="text-sm font-medium truncate text-black dark:text-white">
                      <span className="inline-flex items-center gap-1">
                        {t('nav.tokens_left', { count: isTokenCountReady ? count : '…' })}
                        {!isTokenCountReady && <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" />}
                      </span>
                    </p>
                  </div>
                  <div style={{ fontWeight: 500, fontSize: "15px" }} className="flex flex-col text-sm space-y-2 font-semibold text-black dark:text-white">
                    <LocalizedLink to="/features" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.features')}</LocalizedLink>
                    <button
                      type="button"
                      onClick={() => setIsMobileSolutionsOpen((v) => !v)}
                      className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg inline-flex items-center justify-between"
                      aria-expanded={isMobileSolutionsOpen}
                    >
                      <span>{t('nav.solutions', { defaultValue: 'Solutions' })}</span>
                      <svg
                        className={`h-[10px] w-[10px] transition-transform duration-300 ${isMobileSolutionsOpen ? 'rotate-180 opacity-60' : 'opacity-40'}`}
                        viewBox="0 0 10 10"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    {isMobileSolutionsOpen ? (
                      <div className="flex flex-col space-y-1 pl-4">
                        <LocalizedLink to="/for-education" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_education', { defaultValue: 'Education' })}</LocalizedLink>
                        <LocalizedLink to="/for-organisation" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_organisations', { defaultValue: 'Organisations' })}</LocalizedLink>
                        <LocalizedLink to="/for-government" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_government', { defaultValue: 'Government' })}</LocalizedLink>
                        <LocalizedLink to="/enterprise" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_enterprise', { defaultValue: 'Enterprise' })}</LocalizedLink>
                      </div>
                    ) : null}
                    <LocalizedLink to="/for-investor" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.for_investor')}</LocalizedLink>
                    <LocalizedLink to="/pricing" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.pricing')}</LocalizedLink>
                    {/* AI Studio: hidden for Indian/Razorpay users — they don't get Weave access */}
                    {!isIndianLocaleUser && (
                      <LocalizedLink
                        to="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handleAiStudioRedirect();
                          setIsDropdownOpen(false);
                          setIsMobileMenuOpen(false);
                        }}
                        className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg"
                      >
                        {t('nav.ai_studio', { defaultValue: 'AI Studio' })}
                      </LocalizedLink>
                    )}
                    <LocalizedLink to="/chat" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.consult_sarvalok')}</LocalizedLink>
                    <LocalizedLink to="/settings" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">
                      <span className="flex items-center justify-between gap-2">
                        <span className="flex items-center">
                          <Settings className="w-4 h-4 mr-2" />
                          {t('nav.settings')}
                        </span>
                        {hasPendingStripePayment ? <PaymentPendingDot label={pendingStripePaymentLabel} /> : null}
                      </span>
                    </LocalizedLink>
                    <LocalizedLink to="/pricing" onClick={() => { 
                      setIsDropdownOpen(false); 
                      setIsMobileMenuOpen(false);
                    }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">
                      <span className="flex items-center">
                        <Ticket className="w-4 h-4 mr-2" />
                        {t('nav.upgrade_plan')}
                      </span>
                    </LocalizedLink>
                  </div>
                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setIsMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white flex items-center"
                  >
                    <AccountCircle className="w-4 h-4 mr-1" />
                    {t('nav.logout')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ fontWeight: 500, fontSize: "15px" }} className="flex flex-col px-4 py-2 text-sm space-y-2 font-semibold text-black dark:text-white">
                <LocalizedLink to="/features" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.features')}</LocalizedLink>
                <button
                  type="button"
                  onClick={() => setIsMobileSolutionsOpen((v) => !v)}
                  className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg inline-flex items-center justify-between"
                  aria-expanded={isMobileSolutionsOpen}
                >
                  <span>{t('nav.solutions', { defaultValue: 'Solutions' })}</span>
                  <svg
                    className={`h-[10px] w-[10px] transition-transform duration-300 ${isMobileSolutionsOpen ? 'rotate-180 opacity-60' : 'opacity-40'}`}
                    viewBox="0 0 10 10"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {isMobileSolutionsOpen ? (
                  <div className="flex flex-col space-y-1 pl-4">
                    <LocalizedLink to="/for-education" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_education', { defaultValue: 'Education' })}</LocalizedLink>
                    <LocalizedLink to="/for-organisation" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_organisations', { defaultValue: 'Organisations' })}</LocalizedLink>
                    <LocalizedLink to="/for-government" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_government', { defaultValue: 'Government' })}</LocalizedLink>
                    <LocalizedLink to="/enterprise" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.solutions_enterprise', { defaultValue: 'Enterprise' })}</LocalizedLink>
                  </div>
                ) : null}
                <LocalizedLink to="/for-investor" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false); setIsMobileSolutionsOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.for_investor')}</LocalizedLink>
                <LocalizedLink to="/pricing" onClick={() => { setIsDropdownOpen(false); setIsMobileMenuOpen(false) }} className="hover:bg-gray-200 dark:hover:bg-gray-700 p-2 rounded-lg">{t('nav.pricing')}</LocalizedLink>
                <LocalizedLink
                  to="/login"
                  onClick={() => {
                    setIsDropdownOpen(false);
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-gray-100 dark:bg-gray-800 text-black dark:text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition text-center"
                >
                  {t('nav.login')}
                </LocalizedLink>
                <LocalizedLink
                  to="/sign-up"
                  onClick={() => {
                    trackEvent('cta_click', {
                      button_text: t('nav.get_started'),
                      button_location: 'mobile_menu',
                      destination: "/sign-up",
                      custom_parameters: {
                        user_authenticated: false,
                        timestamp: new Date().toISOString()
                      }
                    });
                    setIsDropdownOpen(false);
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition text-center"
                >
                  {t('nav.get_started')}
                </LocalizedLink>
              </div>
            )}
          </div>
        )}
      </header>
      {/* 28px hit-zone — always rendered when shouldSlideHeader for calc(-100% + 28px) height math.
          The visible tab button only appears once the header has actually slid to avoid double-line effect. */}
      {shouldSlideHeader && (
        <div className="hidden md:block w-full h-[28px] relative group cursor-pointer">
          {isHeaderSlid && (
            <button
              type="button"
              onClick={() => {
	                if (isHeaderHovered) {
	                  if (slideOpenTimeoutRef.current) clearTimeout(slideOpenTimeoutRef.current);
	                  if (slideCloseTimeoutRef.current) clearTimeout(slideCloseTimeoutRef.current);
	                  slideOpenTimeoutRef.current = null;
	                  slideCloseTimeoutRef.current = null;
	                  setIsHeaderHovered(false);
	                } else {
	                  if (slideOpenTimeoutRef.current) clearTimeout(slideOpenTimeoutRef.current);
	                  if (slideCloseTimeoutRef.current) clearTimeout(slideCloseTimeoutRef.current);
	                  slideOpenTimeoutRef.current = null;
	                  slideCloseTimeoutRef.current = null;
	                  setIsHeaderHovered(true);
	                }
	              }}
              className="absolute right-12 top-0 w-14 h-6 bg-white dark:bg-gray-800 border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-xl flex items-center justify-center shadow-[0_4px_12px_rgb(0,0,0,0.05)] transition-all duration-300 group-hover:h-8 group-hover:bg-gray-50 dark:group-hover:bg-gray-700"
              aria-label={isHeaderHovered ? 'Hide menu' : 'Show menu'}
            >
              {isHeaderHovered ? (
                <ChevronUp size={16} strokeWidth={2.5} className="text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors" />
              ) : (
                <ChevronDown size={16} strokeWidth={2.5} className="text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors" />
              )}
            </button>
          )}
        </div>
      )}
      </div>
      {/* Top Edge Seam/Shadow — fades in when header closes (from sample).
          Split into 3 sections to carve out a gap so shadow doesn't darken the pull tab */}
      {shouldSlideHeader && (
        <div
          className={`fixed top-0 left-0 w-full hidden md:flex h-[1px] z-40 pointer-events-none transition-all duration-700 delay-100 ${
            isHeaderSlid && !isHeaderHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Left side shadow line */}
          <div className="flex-1 h-full bg-gray-200 shadow-[0_4px_20px_5px_rgba(0,0,0,0.12)]" />
          {/* Transparent gap (w-32) covering the tab area */}
          <div className="w-32 h-full" />
          {/* Right side shadow line (w-4) */}
          <div className="w-4 h-full bg-gray-200 shadow-[0_4px_20px_5px_rgba(0,0,0,0.12)]" />
        </div>
	      )}
	      <ChatConversationTorana visible={shouldSlideHeader && isHeaderSlid && !isHeaderHovered} />
	      {showChatConversationHeaderNav && shouldSlideHeader && isHeaderSlid && !isHeaderHovered ? (
	        <LocalizedLink
	          to="/chat?new=1"
	          onClick={handleConversationBackClick}
	          className="fixed left-4 top-[3px] z-[1000] hidden h-6 items-center gap-1.5 px-1 text-xs font-semibold text-slate-600 transition-colors hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 md:inline-flex dark:text-slate-300 dark:hover:text-white dark:focus-visible:ring-offset-slate-950"
	          aria-label={t('chat.mode_tabs.main_aria', { defaultValue: 'Go to main Sarvalok chat' })}
	        >
	          <span aria-hidden="true">&larr;</span>
	          <span>{t('chat.mode_tabs.main_label', { defaultValue: 'Main' })}</span>
	        </LocalizedLink>
	      ) : null}
	      {showChatConversationHeaderNav && shouldSlideHeader && isHeaderSlid && !isHeaderHovered ? (
	        <>
	          {/* right-12 slide tab + w-14 tab width + 0.75rem gap = 7.25rem. */}
	          <ShareWithFriends
	            className="fixed right-[7.25rem] top-[3px] z-[1000] hidden md:block"
	            sourceSurface="header"
	            variant="floating"
	          />
	        </>
	      ) : null}
	      </>
	      )}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        isSubmitting={isFeedbackSubmitting}
        isSuccess={isFeedbackSuccess}
        error={feedbackError}
        onClose={() => {
          if (isFeedbackSubmitting) return;
          setIsFeedbackOpen(false);
          setIsFeedbackSuccess(false);
          setFeedbackError(null);
        }}
        onSubmit={handleFeedbackSubmit}
      />

      {isChatRoute && (
        <>
          <div
            className={`fixed inset-0 z-[998] bg-black/40 dark:bg-black/60 backdrop-blur-sm transition-opacity ${isChatMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            onClick={() => setIsChatMenuOpen(false)}
          />
          <FocusLock disabled={!isChatMenuOpen} returnFocus>
            <aside
              ref={(el) => {
                chatMenuRef.current = el;
              }}
              role="dialog"
              aria-modal="true"
              className={`sl-chat-drawer fixed top-0 right-0 z-[999] h-full w-full md:w-80 lg:w-[360px] bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700 shadow-2xl transform transition-transform duration-300 ease-out ${isChatMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
                <div className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-widest">
                  Menu
                </div>
                <button
                  onClick={() => setIsChatMenuOpen(false)}
                  className="sl-chat-drawer-close p-2 rounded-lg text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                  aria-label="Close menu"
                  type="button"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="sl-chat-drawer-body h-full overflow-y-auto px-4 py-4 pb-24 space-y-4">
                {user ? (
                  <LocalizedLink
                    to="/settings"
                    onClick={() => setIsChatMenuOpen(false)}
                    className="sl-chat-drawer-card flex items-center gap-3 p-4 rounded-xl border border-gray-200 dark:border-slate-700 bg-gradient-to-br from-blue-50 to-white dark:from-slate-800 dark:to-slate-900 hover:border-gray-300 dark:hover:border-slate-600 transition-colors"
                    aria-label={t('nav.open_settings_aria', { defaultValue: 'Open settings' })}
                  >
                    <div className="sv-premium-avatar-control relative flex-shrink-0">
                      <PremiumAvatarFace user={user} isPaidPlan={isPaidPlan} variant="drawer" />
                      {hasPendingStripePayment ? (
                        <PaymentPendingDot label={pendingStripePaymentLabel} className="absolute -right-0.5 -top-0.5 ring-2 ring-white dark:ring-gray-950" />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm truncate">
                        {user?.displayName?.trim() ? user.displayName : user?.email}
                      </div>
                      {userPlanName ? (
                        <div className="text-xs font-medium text-[#8B6F47] dark:text-[#D4B575] truncate">
                          {userPlanName}
                        </div>
                      ) : null}
                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                        <span className="inline-flex items-center gap-1">
                          {t('nav.tokens_left', { count: isTokenCountReady ? count : '…' })}
                          {!isTokenCountReady && <Loader2 className="h-3.5 w-3.5 animate-spin opacity-60" />}
                        </span>
                      </div>
                    </div>
                  </LocalizedLink>
                ) : null}

                {chatViewMode === 'conversation' ? (
                  <div className="space-y-2">
                    <LocalizedLink
                      to="/chat"
                      onClick={() => {
                        setIsChatMenuOpen(false);
                        try {
                          sessionStorage.setItem('chat_force_new_session', '1');
                        } catch {}
                        window.dispatchEvent(new Event('sarvalok_consult_main_chat'));
                      }}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
                    >
                      <span className="flex-1">{t('nav.consult_sarvalok')}</span>
                    </LocalizedLink>
                    <ShareWithFriends
                      sourceSurface="chat_drawer"
                      variant="drawer"
                      openEventName="sarvalok_open_share_drawer"
                    />
                  </div>
                ) : null}

                <div className="sl-chat-drawer-section rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                  <button
                    onClick={() => toggleChatMenuSection('explore')}
                    className="sl-chat-drawer-trigger w-full flex items-center gap-3 px-4 py-3 text-slate-900 dark:text-slate-100 font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    aria-expanded={chatMenuExpanded.explore}
                    aria-controls="chat-menu-explore"
                    type="button"
                  >
                    <span className="flex-1 text-left">{t('nav.explore', { defaultValue: 'Explore' })}</span>
                    {hasUnseenExploreFeature ? <ExploreFeatureDot /> : null}
                    <span className={`text-slate-500 dark:text-slate-400 transition-transform ${chatMenuExpanded.explore ? 'rotate-180' : ''}`}>⌄</span>
                  </button>
                  <div id="chat-menu-explore" className={`${chatMenuExpanded.explore ? 'block' : 'hidden'} px-2 pb-2`}>
                    <a
                      href="https://rigveda.sarvalok.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        markExploreFeatureSeen('rigveda');
                        setIsChatMenuOpen(false);
                      }}
                      className="sl-chat-drawer-item flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span>{t('nav.rig_veda', { defaultValue: 'Rig Veda' })}</span>
                      {!seenExploreFeatures.rigveda ? <ExploreFeatureDot /> : null}
                    </a>
                    <a
                      href="https://chant.sarvalok.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        markExploreFeatureSeen('chant');
                        setIsChatMenuOpen(false);
                      }}
                      className="sl-chat-drawer-item flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span>{t('nav.chant', { defaultValue: 'Chant' })}</span>
                      {!seenExploreFeatures.chant ? <ExploreFeatureDot /> : null}
                    </a>
                    <LocalizedLink
                      onClick={() => setIsChatMenuOpen(false)}
                      to="/global-risk"
                      className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      {t('nav.global_risk_radar', { defaultValue: 'Global Risk Radar' })}
                    </LocalizedLink>
                    <div className="my-2 border-t border-gray-200 dark:border-slate-700" />
                    <LocalizedLink onClick={() => setIsChatMenuOpen(false)} to="/features" className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                      {t('nav.features')}
                    </LocalizedLink>
                    <button
                      onClick={() => setIsChatSolutionsOpen((v) => !v)}
                      className="sl-chat-drawer-item sl-chat-drawer-trigger w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                      aria-expanded={isChatSolutionsOpen}
                      type="button"
                    >
                      <span>{t('nav.solutions', { defaultValue: 'Solutions' })}</span>
                      <span className={`text-slate-500 dark:text-slate-400 transition-transform ${isChatSolutionsOpen ? 'rotate-180' : ''}`}>⌄</span>
                    </button>
                    {isChatSolutionsOpen ? (
                      <div className="pl-2">
                        <LocalizedLink onClick={() => { setIsChatMenuOpen(false); setIsChatSolutionsOpen(false); }} to="/for-education" className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                          {t('nav.solutions_education', { defaultValue: 'Education' })}
                        </LocalizedLink>
                        <LocalizedLink onClick={() => { setIsChatMenuOpen(false); setIsChatSolutionsOpen(false); }} to="/for-organisation" className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                          {t('nav.solutions_organisations', { defaultValue: 'Organisations' })}
                        </LocalizedLink>
                        <LocalizedLink onClick={() => { setIsChatMenuOpen(false); setIsChatSolutionsOpen(false); }} to="/for-government" className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                          {t('nav.solutions_government', { defaultValue: 'Government' })}
                        </LocalizedLink>
                        <LocalizedLink onClick={() => { setIsChatMenuOpen(false); setIsChatSolutionsOpen(false); }} to="/enterprise" className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                          {t('nav.solutions_enterprise', { defaultValue: 'Enterprise' })}
                        </LocalizedLink>
                      </div>
                    ) : null}
                    <LocalizedLink onClick={() => setIsChatMenuOpen(false)} to="/for-investor" className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                      {t('nav.for_investor')}
                    </LocalizedLink>
                    <LocalizedLink onClick={() => setIsChatMenuOpen(false)} to="/pricing" className="sl-chat-drawer-item block px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                      {t('nav.pricing')}
                    </LocalizedLink>
                    {/* AI Studio: hidden for Indian/Razorpay users — they don't get Weave access */}
                    {!isIndianLocaleUser && (
                      <button
                        onClick={handleAiStudioRedirect}
                        className="sl-chat-drawer-item block w-full text-left px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                        type="button"
                      >
                        {t('nav.ai_studio', { defaultValue: 'AI Studio' })}
                      </button>
                    )}
                  </div>
                </div>

                <div className="sl-chat-drawer-section rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                  <button
                    onClick={() => toggleChatMenuSection('language')}
                    className="sl-chat-drawer-trigger w-full flex items-center gap-3 px-4 py-3 text-slate-900 dark:text-slate-100 font-medium hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    aria-expanded={chatMenuExpanded.language}
                    aria-controls="chat-menu-language"
                    type="button"
                  >
                    <span className="flex-1 text-left">{t('nav.language', { defaultValue: 'Language' })}</span>
                    <span className={`text-slate-500 dark:text-slate-400 transition-transform ${chatMenuExpanded.language ? 'rotate-180' : ''}`}>⌄</span>
                  </button>
                  <div id="chat-menu-language" className={`${chatMenuExpanded.language ? 'block' : 'hidden'} px-4 py-3`}>
                    <LanguageSwitcher variant="panel" />
                  </div>
                </div>

                {user ? (
                  <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
                    <a
                      href="https://sarvalok.com"
                    onClick={() => setIsChatMenuOpen(false)}
                    className="sl-chat-drawer-item block w-full text-left px-4 py-3 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                  >
                      {t('nav.home', { defaultValue: 'Home' })}
                    </a>
                    <LocalizedLink
                      onClick={() => setIsChatMenuOpen(false)}
                      to="/settings"
                      className="sl-chat-drawer-item flex w-full items-center justify-between gap-2 text-left px-4 py-3 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <span>{t('nav.settings')}</span>
                      {hasPendingStripePayment ? <PaymentPendingDot label={pendingStripePaymentLabel} /> : null}
                    </LocalizedLink>
                    <LocalizedLink
                      onClick={() => setIsChatMenuOpen(false)}
                      to="/pricing"
                      className="sl-chat-drawer-item block w-full text-left px-4 py-3 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      {t('nav.upgrade_plan')}
                    </LocalizedLink>
                    <button
                      onClick={() => {
                        setIsChatMenuOpen(false);
                        handleLogout();
                      }}
                    className="sl-chat-drawer-danger block w-full text-left px-4 py-3 rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      type="button"
                    >
                      {t('nav.logout')}
                    </button>
                  </div>
                ) : (
                  <div className="pt-2 border-t border-gray-200 dark:border-slate-700">
                    <a
                      href="https://sarvalok.com"
                    onClick={() => setIsChatMenuOpen(false)}
                    className="sl-chat-drawer-item block w-full text-left px-4 py-3 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                  >
                      {t('nav.home', { defaultValue: 'Home' })}
                    </a>
                    <LocalizedLink
                      onClick={() => setIsChatMenuOpen(false)}
                      to="/login"
                      className="sl-chat-drawer-item block w-full text-left px-4 py-3 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      {t('nav.login')}
                    </LocalizedLink>
                    <LocalizedLink
                      onClick={() => setIsChatMenuOpen(false)}
                      to="/sign-up"
                      className="sl-chat-drawer-item block w-full text-left px-4 py-3 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      {t('nav.free_trial')}
                    </LocalizedLink>
                  </div>
                )}
              </div>
            </aside>
          </FocusLock>
        </>
      )}

      {/* Remove side padding on chat, features, for-organisation, for-government, and for-investor routes for full-bleed */}
      <main
        className={`min-h-screen ${/\/chat$|\/global-risk$|\/features$|\/for-organisation$|\/for-government$|\/for-investor$|\/for-education$/.test(location.pathname) ? '' : 'lg:px-10'}`}
        style={{ paddingTop: 'var(--app-header-h, 64px)' }}
      >
        <Routes>
          {/* Root redirect with smart language detection */}
          <Route path="/" element={<LanguageRedirect />} />
          
          {/* Language-prefixed routes */}
          <Route path="/:lang/*" element={
            <LanguageWrapper>
              <Routes>
                <Route path="/" element={<MainPage user={user} darkMode={isDarkMode} />} />
                <Route path="/chat" element={<ChatPage />} />
                <Route path="/global-risk" element={<GlobalRiskPage />} />
                <Route path="/login" element={<><AuthPage /><Footer /></>} />
                <Route path="/contact-us" element={<><ContactPage /><Footer /></>} />
                <Route path="/privacy-policy" element={<><PrivacyPage /><Footer /></>} />
                <Route path="/pricing-policy" element={<><PricingPage /><Footer /></>} />
                <Route path="/terms-condition" element={<><TermsPage /><Footer /></>} />
                <Route path="/settings" element={<><SettingsPage /><Footer /></>} />
                <Route path="/settings/memory" element={<><SettingsMemoryPage /><Footer /></>} />
                {/* Enterprise Invite Landing */}
                <Route path="/invite/:inviteId" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <InviteLandingPage />
                  </React.Suspense>
                } />
                {/* Enterprise Admin Routes */}
                <Route path="/admin/super" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <SuperAdminDashboard />
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/admin/org" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <OrgAdminDashboard />
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/payment-success" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <PaymentSuccessPage />
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/pricing" element={<><PricingSection /><Footer /></>} />
                <Route path="/proof" element={<HallucinationLandingPage />} />
                <Route path="/plan-details" element={<><PlanDetailsPage /><Footer /></>} />
                <Route path="/documentation" element={<><DocumentationPage /><Footer /></>} />
                <Route path="/for-organisation" element={<><SarvalokForCommunities /><Footer /></>} />
                <Route path="/for-investor" element={<><SarvalokForInvestors /><Footer /></>} />
                <Route path="/for-education" element={<><SarvalokForEducation /><Footer /></>} />
                <Route path="/features" element={<><SarvalokFeatures /><Footer /></>} />
                <Route path="/dashboard" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteDashboard /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-chat" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteChatPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-writer" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteWriterPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-code" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteCodePage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-ocr" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteOCRPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-summary" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteSummarizerPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-email" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteEmailPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-sql" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteSQLPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-grammar" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteGrammarPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-translator" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteTranslatorPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-story" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteStoryPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-quiz" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteQuizPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-resume" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteResumePage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-social" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteSocialMediaPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-recipe" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteRecipePage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-finance" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteFinancePage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-meeting" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteMeetingNotesPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-sentiment" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteSentimentPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-interview" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteInterviewPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ai-image-generator" element={
                  <React.Suspense fallback={aiSuiteFallback}>
                    <>
                      <AISuiteLayout><AISuiteImageGeneratorPage /></AISuiteLayout>
                      <Footer />
                    </>
                  </React.Suspense>
                } />
                <Route path="/ocr" element={<><OCRPage /><Footer /></>} />
                <Route path="/for-government" element={<><SarvalokGovernmentBenefits /><Footer /></>} />
                <Route path="/use-cases" element={<><UseCasesComponent /><Footer /></>} />
                <Route path="/security" element={<><SarvalokSecurity /><Footer /></>} />
                <Route path="/enterprise" element={<><SarvalokEnterprise /><Footer /></>} />
                <Route path="/about-us" element={<><AboutSarvalok /><Footer /></>} />
                <Route path="/why-sarvalok" element={
                  <React.Suspense fallback={
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  }>
                    <WhySarvalokPage />
                  </React.Suspense>
                } />
                <Route path="/sign-up" element={isAdLandingSignUpRoute ? <SignUpPage /> : <><SignUpPage /><Footer /></>} />
                <Route path="/404" element={<NotFound />} />
                <Route path="/503" element={<ServiceUnavailable />} />
                <Route path="/reset-password" element={<ResetPswd />} />
                <Route path="/blog" element={<><BlogPage /><Footer /></>} />
                <Route path="/blog/:slug" element={<><BlogPage /><Footer /></>} />
                <Route path="/blog/admin" element={<><BlogPage adminMode /><Footer /></>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </LanguageWrapper>
          } />
          
          {/* Legacy fallback routes for backward compatibility */}
          <Route path="/chat" element={<LegacyRedirect to="/en/chat" />} />
          <Route path="/global-risk" element={<LegacyRedirect to="/en/global-risk" />} />
          <Route path="/login" element={<LegacyRedirect to="/en/login" />} />
          <Route path="/contact-us" element={<LegacyRedirect to="/en/contact-us" />} />
          <Route path="/privacy-policy" element={<LegacyRedirect to="/en/privacy-policy" />} />
          <Route path="/pricing-policy" element={<LegacyRedirect to="/en/pricing-policy" />} />
          <Route path="/terms-condition" element={<LegacyRedirect to="/en/terms-condition" />} />
          <Route path="/settings" element={<LegacyRedirect to="/en/settings" />} />
          <Route path="/settings/memory" element={<LegacyRedirect to="/en/settings/memory" />} />
          <Route path="/pricing" element={<LegacyRedirect to="/en/pricing" />} />
          <Route path="/plan-details" element={<LegacyRedirect to="/en/plan-details" />} />
          <Route path="/documentation" element={<LegacyRedirect to="/en/documentation" />} />
          <Route path="/for-organisation" element={<LegacyRedirect to="/en/for-organisation" />} />
          <Route path="/for-investor" element={<LegacyRedirect to="/en/for-investor" />} />
          <Route path="/for-education" element={<LegacyRedirect to="/en/for-education" />} />
          <Route path="/features" element={<LegacyRedirect to="/en/features" />} />
          <Route path="/for-government" element={<LegacyRedirect to="/en/for-government" />} />
          <Route path="/use-cases" element={<LegacyRedirect to="/en/use-cases" />} />
          <Route path="/security" element={<LegacyRedirect to="/en/security" />} />
          <Route path="/enterprise" element={<LegacyRedirect to="/en/enterprise" />} />
          <Route path="/invite/:inviteId" element={<LegacyRedirectInvite />} />
          <Route path="/about-us" element={<LegacyRedirect to="/en/about-us" />} />
          <Route path="/why-sarvalok" element={<LegacyRedirect to="/en/why-sarvalok" />} />
          <Route path="/blog" element={<LegacyRedirect to="/en/blog" />} />
          <Route path="/blog/:slug" element={<LegacyRedirectSlug />} />
          <Route path="/blog/admin" element={<LegacyRedirect to="/en/blog/admin" />} />
          <Route path="/sign-up" element={<LegacyRedirect to="/en/sign-up" />} />
          <Route path="/reset-password" element={<LegacyRedirect to="/en/reset-password" />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="/503" element={<ServiceUnavailable />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
          <ConditionalPrivacyConsent />
        </div>
        </>
      )}
    </>
  );
}


// Component to conditionally render UnifiedPrivacyConsent only on homepage.
// Respects SHOW_CONSENT_NONINDIAN_USERS backend flag — when OFF, non-Indian
// users get auto-accepted (same as Indian users) so analytics fires for everyone.
// Uses 'pending' initial state so the banner never flickers on screen before the
// flag resolves — non-Indian users see nothing until we know whether to show it.
const ConditionalPrivacyConsent: React.FC = () => {
  const location = useLocation();
  const isIndianLocale = typeof window !== 'undefined' && Boolean((window as any).__isIndianLocale__);

  // 'pending' = waiting for flag, 'show' = display consent, 'hide' = auto-accepted
  const [consentState, setConsentState] = React.useState<'pending' | 'show' | 'hide'>(
    // If user already accepted consent before, skip the fetch entirely
    isIndianLocale || (typeof localStorage !== 'undefined' && localStorage.getItem('cookiesAccepted') === 'true')
      ? 'hide'
      : 'pending'
  );

  React.useEffect(() => {
    if (consentState !== 'pending') return;
    const API_BASE_URL = import.meta.env.VITE_APP_SERVER;
    if (!API_BASE_URL) { setConsentState('show'); return; }
    fetch(`${API_BASE_URL}/api/features`)
      .then(r => r.ok ? r.json() : null)
      .then(features => {
        if (features && features.show_consent_nonindian === false) {
          // Auto-accept analytics for non-Indian users (same as Indian path in index.html)
          try {
            localStorage.setItem('analytics-consent', 'accepted');
            localStorage.setItem('cookiesAccepted', 'true');
            localStorage.removeItem('cookiesRejected');
          } catch {}
          // Update gtag consent mode to granted
          const gtag = (window as any)?.gtag;
          if (typeof gtag === 'function') {
            gtag('consent', 'update', {
              analytics_storage: 'granted',
              ad_storage: 'granted',
              ad_user_data: 'granted',
              ad_personalization: 'granted',
            });
          }
          // Also sync the wrapper's internal state
          try { analytics.enableAnalytics(); } catch {}
          setConsentState('hide');
        } else {
          setConsentState('show');
        }
      })
      .catch(() => setConsentState('show')); // fetch failed — show consent as normal
  }, [consentState]);

  // Check if current path is homepage (root path with language prefix)
  const isHomepage = location.pathname === '/' || location.pathname.match(/^\/[a-z]{2}(\/)?$/);

  if (consentState !== 'show') return null;
  return isHomepage ? <UnifiedPrivacyConsent /> : null;
};

export default function AppWrapper() {
  return (
    <AuthProvider>
      <UserProvider>
        <Router>
          <App />
        </Router>
        <Toaster />
      </UserProvider>
    </AuthProvider>
  );
}
